import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import { isEqual, debounce } from "lodash";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, {
  DateClickArg,
  EventDragStartArg,
  EventDragStopArg,
  EventReceiveArg,
  EventResizeDoneArg,
  EventResizeStartArg,
} from "@fullcalendar/interaction";
import resourceDayGridPlugin from "@fullcalendar/resource-daygrid";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import {
  AppointmentDto,
  PatientAvailabilityDto,
  PatientDto,
  RxDto,
  SublocalityDto,
  TimeSlotDto,
  TreatmentNoteDTO,
  UserDto,
} from "../../api-client";
import {
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventAddArg,
  EventApi,
  EventChangeArg,
  EventClickArg,
  EventContentArg,
  EventHoveringArg,
  EventMountArg,
  EventRemoveArg,
} from "@fullcalendar/core";
import moment from "moment";
import { TherapyExt, TimeSlotExt, CellSelectionData } from "./Calendar.type";
import { ClientTimeSlot, isGroupTherapistType, isGroupPatientType } from "./controller/adapters";
import type { EventChangeArgWithGroupTarget } from "./controller/adapters/display-to-client/reverse.adapter";
import { DRAGGABLE_LOA_MAX_HOURS } from "./controller/adapters/client-to-display/timeslot.adapter";
import { EventContent } from "./EventContent";
import "./Calendar.css";
import { Alert, Box, Button, Grid } from "@mui/material";
import { TimeSlotDialog } from "../../components/TerminPlan/TimeSlotDialog";
import UserPopover from "./UserPopover";
import { ResourceLabelContentArg, ResourceLabelMountArg } from "@fullcalendar/resource";
import AddEventModal from "./AddEventModal";
import TimeSlotLabel from "./TimeSlotLabel";
import StickyTimeSlotLabel from "./StickyTimeSlotLabel";
import ResourceHeaderWithDelta from "./ResourceHeaderWithDelta";
// ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging (see dragTrace.ts).
// import { traceDragStart, traceChange, traceDragStop } from "./dragTrace";
import { hitTestDropTarget } from "./dragRecovery";
import { mobiliTheme } from "../../themes/mobiliTheme";
// Removed direct ContextMenu usage; logic now centralized in controller component
import {
  ContextMenuWithController,
  ContextMenuControllerRef,
} from "./ContextMenu/ContextMenuWithController";
import useStore from "../../helpers/useStore";
import { AppointmentContext } from "../../stores/Appointment/appointment.provider";
import HeaderToolbar from "./HeaderToolbar";
import type { CalendarDateNavigation } from "./HeaderToolbar";
import notificationStore from "../../stores/Notification/notificationStore";
import WarningDialog from "../../atoms/WarningDialog";
import LunchBreakPromptDialog from "./lunchBreak/LunchBreakPromptDialog";
import HorizonWarningDialog from "./HorizonWarningDialog";
import { useHorizon } from "./useHorizon";
import { TopLevelPaper } from "../../themes/StyledComponents";
import { EventImpl } from "@fullcalendar/core/internal";
import useCalendarValidations from "./Calendar.validations";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";

import { updateReceivedAppointment } from "../../helpers/calendar";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { CalendarEvent } from "./controller/adapters";
import {
  isPassiveTherapy,
  locallyCheckableTherapyIds,
  slotTherapy,
  slotTherapyList,
} from "../../helpers/slotTherapy";
import { CalendarResource } from "./controller/adapters/types";
import {
  eventToCalendarEvent,
  eventToClientTimeSlot,
} from "./controller/adapters/display-to-client/reverse.adapter";
import {
  adaptContextActionToCalendarEvent,
  buildShelfContextAction,
  buildWaitListContextAction,
} from "./controller/adapters/contextAction.adapter";
import { useLabeledState, useStateSpy } from "../../utils/debug-utils";
import { useUserIdsByDateAndRole } from "../../stores/Calendar/calendar.queries";
import { useNavigate } from "react-router-dom";
import { CalendarHotkeyDeps, useCalendarHotkeys } from "./hotkeys/calendarHotkeys";
import { timeToSlotMin, toCellKey, useCellNavigation } from "./CellNavigation";
import { useApi } from "../../api/ApiProvider";
import TimeSlotIndicator from "./TimeSlotIndicator";
import { useEventShelfController } from "./EventShelf/EventShelfController";
import { useDynamicTemplateProviderController } from "./DynamicTemplateProvider";
moment.locale("de");

interface CalendarProps {
  calendarEvents: CalendarEvent[];
  selectedShelfAppointment?: AppointmentDto;
  patientAvailabilities?: PatientAvailabilityDto[];
  patientUnavailableTypes?: TimeSlotDto[];
  users?: UserDto[]; // Keep for backward compatibility for now
  resources?: CalendarResource[];
  date: Date;
  headerWidth?: string;
  onDatesSet: (date: Date) => void;
  // Infinite-scroll window extension (single-therapist view only).
  onReachWindowStart?: () => void;
  onReachWindowEnd?: () => void;
  onCreateEvent?: (event: CalendarEvent) => void;
  // Support both old and new event handler types during transition
  onChangeEvent?: (eventChangeInfo: EventChangeArg) => void;
  onEventReceive?: (event: EventReceiveArg) => void;
  onRemoveEvent?: (id: string, event?: CalendarEvent) => void;
  onRemoveWorkingHours?: (exceptionId: string) => void;
  onRemoveContinuousEvent?: (id: string) => void;
  onRemoveGroupTherapist?: (id: string) => void;
  onOutdated: () => void;
  onContinuousViewChange: (continuous: boolean) => void;
  isContinuous?: boolean;
  onCancelledViewChange: (checked: boolean) => void;
  isLoading?: boolean;
  onMoveEventDate?: (event: EventApi) => void;
  // isShelfEvents: boolean;
  onAddShelfEvent?: (event: EventApi) => void;
  onEventContentClick?: (event: ClientTimeSlot) => void;
  onCloseNotesDialog?: () => void;
  onSelectedShelfEventReceive?: (appointment: AppointmentDto) => void;
  lockedAppointments?: string[];
  groupCheck: boolean;
  sublocalities?: SublocalityDto[];
  // Warning dialog props (moved from wrapper)
  openWarningDialog?: boolean;
  warningMessage?: string;
  onCloseWarningDialog?: () => void;
  // Cancel day handler
  onCancelDayClick?: (userId: string, date: string, userName: string) => void;
  patchEmptyBusinessHours?: boolean;
  // Cell selection props for keyboard navigation persistence
  /** Initial cell selection to restore focus position after date navigation */
  cellSelection?: CellSelectionData;
  isTimesheetMode?: boolean;
}

// ----- static FullCalendar config (module scope = stable identity) -----
//
// These props are semantically CONSTANT but, when written inline in JSX, get a
// brand-new object/array identity on every render. FullCalendar's React wrapper
// treats any changed prop identity as "options changed" and reprocesses — which
// mid-drag aborts the in-flight interaction. Hoisting them to module scope gives
// every render the exact same reference, so they never contribute to churn and
// never appear in the memoized element's dependency list.
const CALENDAR_PLUGINS = [
  dayGridPlugin,
  timeGridPlugin,
  interactionPlugin,
  resourceDayGridPlugin,
  resourceTimeGridPlugin,
  resourceTimelinePlugin,
];

// An empty spec list means "render the `resources` array in the order given".
//
// FullCalendar places each resource with insertResourceNodeInSiblings, which
// compares candidates against these specs; with no specs every comparison is 0
// and resources are simply appended. This previously read "id", which ordered
// the columns by user UUID and silently discarded whatever order the
// controllers had produced — that is why the configured calendar column order
// never reached the screen. Array order also keeps the trailing
// patient-availability and routeplan resources at the end, where the adapters
// append them.
//
// Module scope for the identity reason described above: an inline [] would be a
// new reference every render.
const KEEP_RESOURCE_ARRAY_ORDER: [] = [];

const CALENDAR_BUTTON_TEXT = {
  today: "Heute",
  month: "Monat",
  year: "Jahr",
  week: "Woche",
  day: "Tag",
  list: "Liste",
};

const CALENDAR_VIEWS = {
  timeGridWeek: {
    dayHeaderFormat: { weekday: "long" as const },
  },
};

const SLOT_LABEL_FORMAT = {
  hour: "2-digit" as const,
  minute: "2-digit" as const,
  meridiem: false as const,
  hour12: false,
};

const EVENT_TIME_FORMAT = {
  hour: "2-digit" as const,
  minute: "2-digit" as const,
  meridiem: false as const,
  hour12: false,
};

const TITLE_FORMAT_CONTINUOUS = { weekday: "long" as const, separator: " bis " };
const TITLE_FORMAT_DEFAULT = {
  year: "numeric" as const,
  month: "long" as const,
  day: "numeric" as const,
  weekday: "long" as const,
};

const SLOT_MIN_TIME = "7:00:00";
const SLOT_MAX_TIME = "21:00:00";

const Calendar: React.FC<CalendarProps> = React.memo(
  ({
    calendarEvents,
    patientAvailabilities,
    patientUnavailableTypes,
    users: propsUsers,
    resources: propsResources,
    isContinuous = false,
    selectedShelfAppointment,
    date,
    headerWidth,
    onDatesSet,
    onReachWindowStart,
    onReachWindowEnd,
    onCreateEvent,
    onEventReceive,
    onChangeEvent,
    onRemoveEvent,
    onRemoveWorkingHours,
    onRemoveContinuousEvent,
    onRemoveGroupTherapist,
    onOutdated,
    onContinuousViewChange,
    onMoveEventDate,
    onAddShelfEvent,
    onEventContentClick,
    onCloseNotesDialog,
    onSelectedShelfEventReceive,
    lockedAppointments = [],
    openWarningDialog,
    warningMessage,
    onCloseWarningDialog,
    onCancelDayClick,
    patchEmptyBusinessHours = true,
    cellSelection,
    isTimesheetMode = false,
  }: CalendarProps) => {
    useEffect(() => {
      dateRef.current = date; // keep ref in sync with prop (for context menu handlers)
    });
    const dateRef = useRef<Date>(date);

    const AppointmentStore = useStore(AppointmentContext);
    const {
      adjustOutsideAppointments,
      // warningDialogMessage,
      setWarningDialogMessage,
      saveAppointments,
    } = AppointmentStore;
    const CalendarStore = useStore(CalendarContext);
    const {
      createTimeslot,
      setSelectedPatientId,
      setPatientDrawerOpen,
      setHighlightedTimeSlot,
      setIsKeyboardNavigation,
      setOriginalDragTime,
      setIsMouseHover,
      setMouseHoverTimeSlot,
      highlightedTimeSlot,
      originalDragTime,
    } = CalendarStore;
    // const selectedAppointmentRef = useRef<ClientTimeSlot | null>(null);
    const calendarRef = useRef<FullCalendar | null>(null);
    const [openEventModal, setOpenEventModal] = useStateSpy(false, "Open Event Modal");
    const eventRef = useRef<CalendarEvent>();
    const [isModalOpen, setModalOpen] = useLabeledState(false, "Modal Open");
    const [clickedTS, setModalTS] = useState<any>();
    const [treatmentNotes, setTreatmentNotes] = useState<string>("");

    // Stable titleFormat identity (only flips with isContinuous). Inline it would
    // be a new object every render and force the memoized element to recompute.
    const titleFormat = useMemo(
      () => (isContinuous ? TITLE_FORMAT_CONTINUOUS : TITLE_FORMAT_DEFAULT),
      [isContinuous],
    );

    // Planning horizon (backend-configured). The main calendar is a single-day
    // view, so "past the horizon" is simply the viewed date being beyond it.
    // Only relevant for the concrete calendar (continuous view is projected).
    const { horizonDateStr, isPastHorizon } = useHorizon();
    const isViewPastHorizon = !isContinuous && !isTimesheetMode && isPastHorizon(date);

    // ----- stable handler trampolines -----
    //
    // Every FullCalendar handler is redefined on each render (each closes over
    // fresh props/state). Passing them directly gives the element new prop
    // identities every render → FullCalendar reprocesses its options → mid-drag
    // aborts. Instead we keep the LATEST handlers in `handlersRef` (reassigned
    // every render, below) and hand FullCalendar these STABLE trampolines, created
    // exactly once, that forward to `handlersRef.current` at call time. The
    // trampolines never change identity, so they keep the memoized element stable
    // while still invoking the current logic.
    const handlersRef = useRef<any>({});
    /**
     * FullCalendar calls its handlers fire-and-forget — the promise an async
     * handler returns is dropped. So a rejection inside one (a server-refused
     * move, say) has nowhere to land and becomes an `unhandledrejection`: in
     * dev the full-screen "Uncaught runtime errors" overlay, even when the
     * refusal was already reported to the user and rolled back. Attaching this
     * catch marks the promise handled; whoever dispatched the work stays
     * responsible for reporting it.
     */
    const settleQuietly = (label: string, result: any) => {
      if (result instanceof Promise) {
        result.catch((error: unknown) =>
          console.error(`[CALENDAR EVENT HANDLER] ${label} failed`, error),
        );
      }
      return result;
    };
    const stableHandlers = useRef({
      handleSelect: (a: DateSelectArg) => handlersRef.current.handleSelect(a),
      handleSelectable: (a: DateSpanApi) => handlersRef.current.handleSelectable(a),
      handleDateClick: (a: DateClickArg) => handlersRef.current.handleDateClick(a),
      handleEventAdd: (a: EventAddArg) => handlersRef.current.handleEventAdd(a),
      handleEventChange: (a: EventChangeArg) =>
        settleQuietly("eventChange", handlersRef.current.handleEventChange(a)),
      handleEventRemove: (a: EventRemoveArg) => handlersRef.current.handleEventRemove(a),
      handleEventReceive: (a: EventReceiveArg) =>
        settleQuietly("eventReceive", handlersRef.current.handleEventReceive(a)),
      handleEventResize: (a: EventResizeDoneArg) =>
        settleQuietly("eventResize", handlersRef.current.handleEventResize(a)),
      handleEventResizeStart: (a: EventResizeStartArg) =>
        handlersRef.current.handleEventResizeStart(a),
      handleEventClick: (a: EventClickArg) => handlersRef.current.handleEventClick(a),
      handleEventDidMount: (a: EventMountArg) => handlersRef.current.handleEventDidMount(a),
      handleEventMouseEnter: (a: EventHoveringArg) => handlersRef.current.handleEventMouseEnter(a),
      eventDragStart: (a: EventDragStartArg) => handlersRef.current.eventDragStart(a),
      eventDragStop: (a: EventDragStopArg) => handlersRef.current.eventDragStop(a),
      eventAllow: (a: DateSpanApi, b: EventApi | null) => handlersRef.current.eventAllow(a, b),
      handleEventOverlap: (a: EventApi, b: EventApi | null) =>
        handlersRef.current.handleEventOverlap(a, b),
      renderEventContent: (a: EventContentArg) => handlersRef.current.renderEventContent(a),
      handleEventClassNames: (a: { event: EventImpl }) =>
        handlersRef.current.handleEventClassNames(a),
      handleEvents: (a: EventApi[]) => handlersRef.current.handleEvents(a),
      resourceLabelContent: (a: ResourceLabelContentArg) =>
        handlersRef.current.resourceLabelContent(a),
      handleResourceLabelDidMount: (a: ResourceLabelMountArg) =>
        handlersRef.current.handleResourceLabelDidMount(a),
      handleViewDidMount: (a: any) => handlersRef.current.handleViewDidMount(a),
      eventWillUnmount: (a: any) => handlersRef.current.eventWillUnmount(a),
    }).current;

    const { clinicalDocumentation } = useApi();

    // Controller ref for centralized context menu
    const contextMenuRef = useRef<ContextMenuControllerRef | null>(null);

    const eventShelfController = useEventShelfController();
    const eventShelfControllerRef = useRef(eventShelfController);
    // Template-widget view for the "a" source-cycling hotkey. The controller is
    // fully store/query-derived (no local state), so this instance sees exactly
    // what the EventShelf's own instance renders.
    const templateProviderController = useDynamicTemplateProviderController();
    const templateProviderControllerRef = useRef(templateProviderController);
    // Populated by HeaderToolbar with its date-navigation actions (j/k/J/K).
    const toolbarNavigationRef = useRef<CalendarDateNavigation | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserDto>();
    const [showNotesDialog, setShowNotesDialog] = useState<boolean>(false);

    const {
      frequencyValidation,
      hasTherapistCredentials,
      isPatientAppointmentOverlapping,
      isPatientEventOverlapping,
      hasPatientAppointmentOnDate,
      isDoppelPairing,
    } = useCalendarValidations();
    const [patientAvailability] = useState<PatientAvailabilityDto>();

    const navigate = useNavigate();
    const patientsStore = useStore(PatientsContext);
    const { navigateToPatient } = PatientCommands(patientsStore);
    const [waitListOpen, setWaitListOpen] = useState(false);

    // Debounced function to load patient overview when navigating appointments
    const debouncedLoadPatientOverview = useCallback(
      debounce((patientId: number | null) => {
        if (patientId) {
          setSelectedPatientId(patientId);
          setPatientDrawerOpen(true);
        }
      }, 500), // 500ms debounce delay
      [setSelectedPatientId, setPatientDrawerOpen],
    );

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        debouncedLoadPatientOverview.cancel();
      };
    }, [debouncedLoadPatientOverview]);

    const resources = useMemo(() => propsResources || [], [propsResources]);

    // refs for dom wiring (idempotent)
    const wiredVersionRef = useRef<number>(0); // increments when we re-wire
    const prevResourceKeyRef = useRef<string>("");
    const lastFocusedSlotRef = useRef<HTMLElement | null>(null);
    const hasInitializedFocusRef = useRef<boolean>(false); // Track if we've initialized focus on first arrow key
    const originalDragTimeRef = useRef<string | null>(null); // Store original time during drag

    // ----- mid-interaction events freeze -----
    //
    // `events` is a CONTROLLED FullCalendar prop fed from the query cache. Handing
    // FullCalendar a NEW events array reference WHILE a drag/resize is physically
    // in progress makes it reprocess and resync every event to the cache
    // positions — which ABORTS the in-flight drag. The drop's `eventChange` then
    // never fires, so the reschedule mutation is never dispatched and the move is
    // silently lost (the appointment looks placed but the DB keeps the old slot).
    // This was confirmed with the drag tracer: real losses showed "eventChange
    // NEVER fired (drag aborted mid-interaction)", occurring right as a prior
    // slow reschedule's onSuccess rebuilt the events array.
    //
    // Fix: render a FROZEN snapshot (`displayedEvents`) to FullCalendar. The user
    // keeps interacting with ZERO added latency — we only withhold NEW array
    // references from FullCalendar while a drag/resize is active, stashing the
    // latest cache-derived array and flushing it the instant the interaction
    // ends. Nothing about the user's input is blocked or delayed.
    const [displayedEvents, setDisplayedEvents] = useState(calendarEvents);
    const interactionActiveRef = useRef(false);
    const latestEventsRef = useRef(calendarEvents);
    const releaseFreezeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const safetyReleaseRef = useRef<(() => void) | null>(null);
    // Set when a drop has ended the freeze and we are waiting for the optimistic
    // cache patch (which contains the move) to arrive before flushing, so we
    // never push a pre-move snapshot to FullCalendar. See endInteractionFreeze.
    const awaitingFlushRef = useRef(false);
    // How long to wait, after a drop, for the optimistic patch before force-
    // flushing the stashed array. Only ever reached for moves that produce no
    // patch (e.g. a no-op drop onto the same slot), where old === new so there is
    // nothing to flash. The async onMutate patch lands in a few microtasks, well
    // inside this window, so the real flush is patch-driven, not timer-driven.
    const FREEZE_FLUSH_FALLBACK_MS = 150;

    // ----- drag-drop safety net (layer 2): recover aborted drops -----
    //
    // If a drag is aborted mid-interaction (FullCalendar tears down the dragged
    // element when an option is reprocessed), the drop fires NO eventChange and
    // the move is silently lost (snap-back). Layer 1 (the events freeze above)
    // prevents most aborts; this is the catch layer for any that slip through.
    //
    // On drag start we capture the source (event id + original start/end). On
    // drop we record whether FullCalendar delivered an eventChange; if it did
    // NOT within a short window, we hit-test the pointer-up position against the
    // grid DOM (independent of FullCalendar's React state) and replay the move
    // through FullCalendar's own API, which fires a normal eventChange and
    // re-enters the regular reschedule pipeline.
    const dragSourceRef = useRef<{
      eventId: string;
      apptId: string;
      patient: string;
      origStartMs: number;
      origEndMs: number;
    } | null>(null);
    const eventChangeFiredRef = useRef(false);
    const dropRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Vertical offset (px) between the pointer and the TOP of the grabbed event
    // at drag start. FullCalendar positions the dropped event's top at
    // (pointer - grabOffset); the recovery hit-test must subtract it or the
    // replayed move lands one grab-offset too late (the "5 min later" bug).
    const grabOffsetYRef = useRef(0);
    // Mirrors FullCalendar's drag-time drop validation (eventAllow + eventOverlap)
    // so the recovery replay can REJECT an invalid target instead of force-placing
    // it. A drop that FullCalendar reverted because it was disallowed (e.g. on top
    // of another appointment) fires no eventChange — indistinguishable from a true
    // abort — so recovery must re-run the same checks before replaying. Assigned
    // each render once eventAllow/handleEventOverlap are in scope (see below).
    const validateRecoveryDropRef = useRef<
      (ev: EventApi, start: Date, end: Date, resourceId: string | null) => boolean
    >(() => true);



    useEffect(() => {
      latestEventsRef.current = calendarEvents;
      // Only push a new array through to FullCalendar when no interaction is in
      // flight; otherwise hold it until the interaction ends.
      if (!interactionActiveRef.current) {
        // This fresh, cache-derived array is the post-drop optimistic patch (it
        // already reflects the just-dropped move), so flushing it cannot snap the
        // event back. If a freeze-release was waiting for exactly this, satisfy it
        // now and cancel the stale-snapshot fallback.
        if (awaitingFlushRef.current) {
          awaitingFlushRef.current = false;
          if (releaseFreezeTimerRef.current) {
            clearTimeout(releaseFreezeTimerRef.current);
            releaseFreezeTimerRef.current = null;
          }
        }
        setDisplayedEvents(calendarEvents);
      }
    }, [calendarEvents]);

    const endInteractionFreeze = useCallback(() => {
      // Detach the pointer-up safety net (if armed).
      if (safetyReleaseRef.current) {
        window.removeEventListener("pointerup", safetyReleaseRef.current);
        window.removeEventListener("pointercancel", safetyReleaseRef.current);
        safetyReleaseRef.current = null;
      }
      // Release the freeze, but do NOT immediately flush the currently-stashed
      // array. At drop time that stash still holds the PRE-MOVE positions: the
      // drop's onMutate optimistic cache patch is async (it awaits cancelQueries
      // before writing), so it usually has not landed yet. Pushing the stash now
      // would hand FullCalendar a controlled array with the just-dropped event
      // back at its OLD slot for one frame before the patch arrives — the brief
      // "flash to old position" the user still sees.
      //
      // Instead, stop freezing and let the very next cache-derived array (the
      // optimistic patch, which already contains the move) flush through the
      // effect above, so the first array FullCalendar receives after a drop is the
      // one with the event in its FINAL place. The fallback timer only fires for
      // moves that produce no patch (e.g. a no-op drop onto the same slot), where
      // old === new and there is nothing to flash.
      interactionActiveRef.current = false;
      awaitingFlushRef.current = true;
      if (releaseFreezeTimerRef.current) clearTimeout(releaseFreezeTimerRef.current);
      releaseFreezeTimerRef.current = setTimeout(() => {
        releaseFreezeTimerRef.current = null;
        if (awaitingFlushRef.current) {
          awaitingFlushRef.current = false;
          setDisplayedEvents(latestEventsRef.current);
        }
      }, FREEZE_FLUSH_FALLBACK_MS);
    }, []);

    const beginInteractionFreeze = useCallback(() => {
      // A new interaction starting cancels any pending release/flush so a
      // back-to-back drag never unfreezes between drops.
      if (releaseFreezeTimerRef.current) {
        clearTimeout(releaseFreezeTimerRef.current);
        releaseFreezeTimerRef.current = null;
      }
      awaitingFlushRef.current = false;
      interactionActiveRef.current = true;
      // Safety net: a no-op resize never fires eventResize, so guarantee release
      // on the gesture's pointer-up regardless of which FullCalendar callback
      // does (or does not) fire. Idempotent with the explicit handler releases.
      if (!safetyReleaseRef.current) {
        const release = () => endInteractionFreeze();
        safetyReleaseRef.current = release;
        window.addEventListener("pointerup", release, { once: true });
        window.addEventListener("pointercancel", release, { once: true });
      }
    }, [endInteractionFreeze]);

    // How long after a drag ends to wait for FullCalendar's own eventChange
    // before treating the drop as lost and recovering it. eventChange fires
    // synchronously around the drop, so this only needs to clear the same tick
    // plus a little slack.
    const DROP_RECOVERY_DELAY_MS = 120;

    // Replay a lost drop. Called from a short timer armed on drag-stop; bails
    // immediately if FullCalendar actually delivered the move (the common case).
    const recoverLostDrop = useCallback((clientX: number, clientY: number) => {
      // FullCalendar handled the drop normally — nothing to recover.
      if (eventChangeFiredRef.current) return;

      const src = dragSourceRef.current;
      const root = calendarRootRef.current;
      const api = calendarRef.current?.getApi();
      if (!src || !root || !api) return;

      try {
        // Reconstruct the event's TOP edge from the pointer-up position by
        // removing the grab offset: this is the pixel where FullCalendar would
        // place the dropped event's start. elementsFromPoint then returns the
        // slot that CONTAINS that pixel (a floor), but FullCalendar snaps to the
        // NEAREST slot (a round). Without correction, a top landing in the lower
        // half of a slot lands one slot (5 min) too early. Adding half a slot
        // height before hit-testing turns the floor into round-to-nearest.
        const slotEl = root.querySelector<HTMLElement>(
          ".fc-timegrid-slot[data-time], .fc-timegrid-slot-resource[data-time]",
        );
        const slotHeight = slotEl?.getBoundingClientRect().height ?? 0;
        const effectiveY = clientY - grabOffsetYRef.current + slotHeight / 2;
        const target = hitTestDropTarget(root, clientX, effectiveY);
        if (!target) {
          // Dropped outside the grid (or cancelled away from it): no move.
          return;
        }

        const ev = api.getEventById(src.eventId);
        if (!ev) return;

        // Build the target start from the viewed day + the slot's data-time, and
        // preserve the original duration. Resource views show a single day across
        // therapist columns, so the date is the currently viewed date.
        const [h, m, s] = target.time.split(":").map((n) => parseInt(n, 10));
        const newStart = new Date(dateRef.current);
        newStart.setHours(h, m, s || 0, 0);
        const duration = src.origEndMs - src.origStartMs;
        const newEnd = new Date(newStart.getTime() + (duration > 0 ? duration : 0));

        const currentResourceId = ev.getResources()?.[0]?.id;
        const targetResourceId = target.resourceId ?? currentResourceId ?? null;
        const movedTime = newStart.getTime() !== src.origStartMs;
        const movedResource = !!target.resourceId && target.resourceId !== currentResourceId;
        if (!movedTime && !movedResource) {
          // Dropped back on the original slot — genuine no-op, don't replay.
          return;
        }

        // Re-run FullCalendar's own drop validation. A drop that FC reverted
        // because it was disallowed (overlap, patient unavailable, PA column, …)
        // fires no eventChange — identical to a true abort — so without this gate
        // recovery would force-place an invalid move. If the target is invalid we
        // leave the appointment at its origin, exactly as a rejected drag would.
        if (!validateRecoveryDropRef.current(ev, newStart, newEnd, targetResourceId)) {
          console.warn(
            `[DRAG-RECOVERY] Skipped invalid drop for "${src.patient ?? src.apptId}" → ${target.time} (failed drop validation)`,
          );
          return;
        }

        console.warn(
          `[DRAG-RECOVERY] Replaying lost drop for "${src.patient ?? src.apptId}" → ${target.time}` +
            (movedResource ? ` (resource ${currentResourceId} → ${target.resourceId})` : ""),
        );

        // Replay through FullCalendar's API. Each mutation fires a native
        // eventChange that re-enters the normal reschedule pipeline; the
        // appointment-mutation sequence guard dedups if both fire.
        if (movedResource && target.resourceId) {
          ev.setResources([target.resourceId]);
        }
        ev.setDates(newStart, newEnd);
      } catch (err) {
        console.error("[DRAG-RECOVERY] Failed to recover lost drop:", err);
      } finally {
        dragSourceRef.current = null;
      }
    }, []);


    useEffect(
      () => () => {
        if (releaseFreezeTimerRef.current) clearTimeout(releaseFreezeTimerRef.current);
        if (dropRecoveryTimerRef.current) clearTimeout(dropRecoveryTimerRef.current);
        if (safetyReleaseRef.current) {
          window.removeEventListener("pointerup", safetyReleaseRef.current);
          window.removeEventListener("pointercancel", safetyReleaseRef.current);
        }
      },
      [],
    );

    // Duplicate-day warning dialog state
    const [duplicateWarning, setDuplicateWarning] = useState<{
      open: boolean;
      message: string;
      duplicateEvents: CalendarEvent[];
    }>({ open: false, message: "", duplicateEvents: [] });
    const duplicateWarningCallbacks = useRef<{
      onConfirm: () => void;
      onCancel: () => void;
    } | null>(null);
    /**
     * True while the warning is on screen. A second move cannot be started with
     * a modal open, but a programmatic one can (drop recovery, keyboard place),
     * and two overlapping prompts share one dialog — the second would overwrite
     * the first's callbacks and leave its promise pending forever.
     */
    const duplicateWarningOpenRef = useRef(false);

    const calendarRootRef = useRef<HTMLDivElement | null>(null);

    // Arm the events freeze on pointer-DOWN (capture phase), before FullCalendar
    // even begins processing a drag. The freeze previously armed in
    // eventDragStart, but FullCalendar fires that AFTER pointerdown and after the
    // drag has internally begun — leaving a small unguarded window in which a
    // background reschedule's onSuccess could rebuild the events array, flush it
    // to FullCalendar, and abort the nascent drag (eventChange never fires, the
    // move is lost). Capturing pointerdown closes that window for the whole
    // gesture; release stays on pointerup/pointercancel (armed inside
    // beginInteractionFreeze). A bare click also briefly freezes and releases on
    // pointerup, which is harmless.
    useEffect(() => {
      const root = calendarRootRef.current;
      if (!root) return;
      const onPointerDown = (e: PointerEvent) => {
        beginInteractionFreeze();
        // Record where within the grabbed event the pointer landed, so the drop
        // safety net can reconstruct the event's TOP (= its start slot) from the
        // pointer-up position. Non-event pointerdowns reset it to 0 (unused unless
        // an event drag actually starts).
        const harness = (e.target as HTMLElement | null)?.closest?.(
          ".fc-timegrid-event-harness, .fc-event",
        ) as HTMLElement | null;
        grabOffsetYRef.current = harness
          ? e.clientY - harness.getBoundingClientRect().top
          : 0;
      };
      root.addEventListener("pointerdown", onPointerDown, { capture: true });
      return () => root.removeEventListener("pointerdown", onPointerDown, { capture: true } as any);
    }, [beginInteractionFreeze]);

    // One-shot layout correction after (re)mount.
    //
    // `adjustTableTimeCells` (run from `handleViewDidMount`) mutates the slot
    // table — it sets rowspan=3 on every third time-label cell and hides the
    // other two to group the 5-minute slot labels into 15-minute blocks. On a
    // fresh mount FullCalendar measures the slats BEFORE that mutation, so the
    // first paint is correct. But when the calendar is remounted (e.g. leaving
    // for the patient page and coming back), a data-driven re-render makes
    // FullCalendar RE-MEASURE the already-mutated slot DOM. The merged rows make
    // it read slat coordinates that are exactly 3× too large, so every
    // appointment renders 3× too tall and 3× too low. The bad coordinates are
    // cached: `updateSize()` / window resize re-measure the same mutated DOM and
    // do NOT recover. Only rebuilding the view regenerates the slot table without
    // the rowspans, yielding a clean measurement (after which `adjustTableTimeCells`
    // re-applies the rowspans, and later event-only updates never re-measure the
    // slats — so a single corrective pass is durable).
    //
    // Watch for the corruption with a short rAF poll and, the moment it appears,
    // rebuild the view once. Detection is data-independent: a healthy layout keeps
    // every event within the slot grid, whereas the 3× corruption pushes events
    // far past the grid bottom. The poll is guarded to correct a single time and
    // skipped while an interaction is active, so it can never disrupt a drag.
    const layoutCorrectedRef = useRef(false);
    useEffect(() => {
      if (layoutCorrectedRef.current) return;
      if (!displayedEvents || displayedEvents.length === 0) return;

      const isCorrupted = (): boolean => {
        const root = calendarRootRef.current;
        if (!root) return false;
        const slotsTable = root.querySelector<HTMLElement>(".fc-timegrid-slots table");
        if (!slotsTable) return false;
        const slotsRect = slotsTable.getBoundingClientRect();
        if (slotsRect.height === 0) return false;
        let maxBottom = 0;
        root.querySelectorAll<HTMLElement>(".fc-timegrid-event-harness").forEach((h) => {
          const bottom = h.getBoundingClientRect().bottom - slotsRect.top;
          if (bottom > maxBottom) maxBottom = bottom;
        });
        return maxBottom > slotsRect.height * 1.2;
      };

      let rafId = 0;
      const deadline = performance.now() + 3000;
      const tick = () => {
        if (layoutCorrectedRef.current) return;
        if (!interactionActiveRef.current && isCorrupted()) {
          const api = calendarRef.current?.getApi();
          if (api) {
            layoutCorrectedRef.current = true;
            // Rebuild the current view -> fresh slot table -> clean re-measure.
            api.changeView(api.view.type);
            return;
          }
        }
        if (performance.now() < deadline) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }, [displayedEvents]);

    // Assigned further down, next to the infinite-scroll wiring it shares state
    // with. The indirection keeps the cell-navigation options here without
    // depending on declaration order.
    const requestWindowExtensionRef = useRef<(edge: "start" | "end") => void>(() => {});

    const cellNav = useCellNavigation({
      getRootEl: () => calendarRootRef.current,
      hasPatientResource: resources.some((r) => r.id === "0"),
      onReachEdge: (edge) => requestWindowExtensionRef.current(edge),
      onSelectedAppointmentChange: (slot) => {
        console.debug("[CellNav] onSelectedAppointmentChange", slot);
        if (CalendarStore.peekMode) {
          CalendarStore.peekMode = false;
          return;
        }
        debouncedLoadPatientOverview(slot?.patient?.id ?? null);
      },
      onCellFocusChange: (info) => {
        // Update highlighted time slot when navigating with keyboard
        // When on an event, use the event's start time; otherwise use the cell time
        // console.log("[CellNav] onCellFocusChange", info.eventStartTime, info.cellTime);
        const timeToHighlight =
          info.isOnEvent && info.eventStartTime ? info.eventStartTime : info.cellTime;

        setHighlightedTimeSlot(timeToHighlight);
        setIsKeyboardNavigation(true);
        setIsMouseHover(false);
      },
    });

    useEffect(
      () => cellNav.attachDelegatedEventListeners(),
      [cellNav.attachDelegatedEventListeners],
    );

    // Toggle slot lane visibility based on presence of patient resource column
    const toggleSlotLaneVisibility = (shouldShow: boolean) => {
      const rows = document.querySelectorAll("tr");
      rows.forEach((row) => {
        const targetTd = row.querySelector(".fc-timegrid-slot-lane") as HTMLElement;
        if (targetTd) {
          targetTd.style.display = shouldShow ? "block" : "none";
        }
      });
    };

    // Toggle slot lane visibility based on patient availability column presence
    const hasPatientResource = resources.some((r) => r.id === "0");
    const hasPatientAvailabilities = (patientAvailabilities?.length ?? 0) > 0;
    const isSingleTherapistView = CalendarStore.singleTherapistView;
    useEffect(() => {
      // In single user view, hide the vertical column line when the patient has availability records
      if (isSingleTherapistView && hasPatientAvailabilities) {
        toggleSlotLaneVisibility(false);
        return;
      }
      toggleSlotLaneVisibility(hasPatientResource);
    }, [hasPatientResource, hasPatientAvailabilities, isSingleTherapistView]);

    // Internal ref for getCellSelection function to pass to HeaderToolbar
    const internalGetCellSelectionRef = useRef<(() => CellSelectionData | null) | null>(null);
    // Track if user has navigated locally since last cellSelection restoration
    const hasNavigatedLocallyRef = useRef<boolean>(false);
    // Store the last cellSelection values to detect actual changes
    const lastCellSelectionRef = useRef<{ resourceId: string; time: string } | null>(null);

    const buildResourceKey = (rs: CalendarResource[]) => rs.map((r) => r.id).join("|");

    // --- Resources (provided by CalendarController) ---
    const { setColumnUserIds, setIsRoleMode } = CalendarStore; // selected user ids for calendar columns

    // Use users from props for backward compatibility
    const users = useMemo(() => propsUsers || [], [propsUsers]);

    // Use resources from props directly (already converted by controller)

    const isScrollable = resources.length >= 10;
    const calendarContentWidth = isScrollable ? `max(100%, ${resources.length * 185}px)` : "100%";

    // ----- Infinite horizontal scroll (single-therapist view) -----
    // The day-columns live in an outer overflow:auto Box (the parent of the calendar
    // root). When the user scrolls near the left/right edge we ask the controller to
    // widen its date window. On a left-edge (prepend) extension the existing columns
    // shift right, so we restore scrollLeft by the width delta to keep the viewport
    // visually anchored. An extend lock prevents repeat firing until the new columns render.
    const resourceKey = useMemo(() => buildResourceKey(resources), [resources]);
    const pendingPrependScrollWidthRef = useRef<number | null>(null);
    const extendLockRef = useRef(false);

    // Single entry point for widening the window, shared by the scroll listener
    // (scrolled to an edge) and the cell cursor (arrowed off the edge). The
    // keyboard path is not a convenience: on a display wide enough to show every
    // day column at once there is no horizontal overflow, so no scroll events ever
    // fire and scrolling alone could never grow the window.
    const requestWindowExtension = useCallback(
      (edge: "start" | "end") => {
        if (!isSingleTherapistView || extendLockRef.current) return;
        if (edge === "start") {
          if (!onReachWindowStart) return;
          extendLockRef.current = true;
          pendingPrependScrollWidthRef.current =
            calendarRootRef.current?.parentElement?.scrollWidth ?? null;
          onReachWindowStart();
        } else {
          if (!onReachWindowEnd) return;
          extendLockRef.current = true;
          onReachWindowEnd();
        }
      },
      [isSingleTherapistView, onReachWindowStart, onReachWindowEnd],
    );
    requestWindowExtensionRef.current = requestWindowExtension;

    useEffect(() => {
      if (!isSingleTherapistView || !onReachWindowStart || !onReachWindowEnd) return;
      const scroller = calendarRootRef.current?.parentElement;
      if (!scroller) return;

      const EDGE_THRESHOLD_PX = 300;
      let rafId: number | null = null;

      const evaluate = () => {
        rafId = null;
        if (extendLockRef.current) return;
        const nearStart = scroller.scrollLeft <= EDGE_THRESHOLD_PX;
        const nearEnd =
          scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft <= EDGE_THRESHOLD_PX;
        if (nearStart) {
          requestWindowExtension("start");
        } else if (nearEnd) {
          requestWindowExtension("end");
        }
      };

      const onScroll = () => {
        if (rafId != null) return;
        rafId = requestAnimationFrame(evaluate);
      };

      scroller.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        scroller.removeEventListener("scroll", onScroll);
        if (rafId != null) cancelAnimationFrame(rafId);
      };
    }, [
      isSingleTherapistView,
      onReachWindowStart,
      onReachWindowEnd,
      requestWindowExtension,
      resourceKey,
    ]);

    // After a window extension re-renders the columns, anchor the scroll position (for
    // left-edge prepends) and release the extend lock so further scrolling can trigger
    // the next extension.
    useLayoutEffect(() => {
      const scroller = calendarRootRef.current?.parentElement;
      if (!scroller) return;
      if (pendingPrependScrollWidthRef.current != null) {
        const delta = scroller.scrollWidth - pendingPrependScrollWidthRef.current;
        if (delta > 0) scroller.scrollLeft += delta;
        pendingPrependScrollWidthRef.current = null;
      }
      extendLockRef.current = false;
    }, [resourceKey]);

    let eventGuid = 0;

    // ------------------------------------------------------------------
    // Keyboard bindings — ALL "termin"-scope hotkeys are DEFINED centrally in
    // ./hotkeys/calendarHotkeys.ts (single source of truth incl. docs for the
    // raw-listener keys: arrows + i/j/k/l cell cursor → CellNavigation.ts,
    // Escape → EventShelf.tsx).
    // This component only supplies the wiring through a deps ref that is
    // reassigned every render so handlers always see fresh state. The
    // isActiveInstance guard keeps hidden calendar instances (the Scheduler
    // mounts all three variants and toggles them with display:none) from
    // swallowing keys.
    // ------------------------------------------------------------------
    const openQuickCreateAtCursor = () => {
      const cursor = cellNav.getFocusedCell?.();
      if (!cursor) return;

      const yyyyMmDd = dateRef.current.toISOString().slice(0, 10);
      const startIso = `${yyyyMmDd}T${cursor.timeStr}:00`;

      const start = new Date(startIso);
      const end = new Date(start.getTime() + 5 * 60 * 1000);

      const newEvent: any = {
        id: undefined,
        title: "",
        start: start.toISOString(),
        end: end.toISOString(),
        resourceId: cursor.resourceId,
        timeSlot: { start: start.toISOString(), end: end.toISOString() },
      };

      eventRef.current = newEvent;
      setOpenEventModal(true);
    };

    const hotkeyDepsRef = useRef<CalendarHotkeyDeps>(undefined as unknown as CalendarHotkeyDeps);
    hotkeyDepsRef.current = {
      isActiveInstance: () => calendarRootRef.current?.offsetParent != null,
      calendarStore: CalendarStore,
      calendarApi: () => calendarRef.current?.getApi(),
      contextMenu: () => contextMenuRef.current,
      getSelectedAppointment: () => cellNav.selectedAppointmentRef.current,
      getFocusedCell: () => cellNav.getFocusedCell?.() ?? null,
      getDate: () => dateRef.current,
      getShelfData: () => eventShelfControllerRef.current?.allShelvesData ?? [],
      receiveExternalEvent: (arg) => handlersRef.current.handleEventReceive(arg),
      toggleAttended: (slot) => {
        void toggleAppointmentAttended(slot);
      },
      navigateToPatient: (patientId, rxId, subtab) =>
        navigateToPatient(navigate, patientId, rxId!, subtab),
      openQuickCreateAtCursor,
      getTemplateData: () => ({
        visible: templateProviderControllerRef.current.isVisible,
        handles: templateProviderControllerRef.current.handles,
      }),
      dateNavigation: () => toolbarNavigationRef.current,
    };
    useCalendarHotkeys(hotkeyDepsRef);

    // patch empty buisness hours
    if (patchEmptyBusinessHours && resources.length > 0) {
      // for any resource that has no business hours, add a business hour from 0 to 0 ocolock

      resources.forEach((res) => {
        // console.debug("Checking business hours for resource:", res);
        if (!res.businessHours || res.businessHours?.length === 0) {
          res.businessHours = [
            {
              daysOfWeek: [1, 2, 3, 4, 5],
              startTime: "00:00",
              endTime: "00:05",
            },
          ];
        } else {
          const missingDays = [0, 1, 2, 3, 4, 5, 6].filter(
            (dow) => !res.businessHours?.some((bh) => bh.daysOfWeek?.includes(dow)),
          );
          // console.debug("Resource", res.id, "is missing business hours for days:", missingDays);
          if (missingDays.length > 0) {
            res.businessHours = [
              ...res.businessHours!,
              {
                daysOfWeek: missingDays,
                startTime: "00:00",
                endTime: "00:05",
              },
            ];
          }
        }
      });
    }

    // --- FullCalendar controlled date sync ----------------------------------
    // Ensure the FullCalendar internal view updates when the external date prop changes.
    useEffect(() => {
      const api = calendarRef.current?.getApi();
      if (!api || !date) return;
      const current = api.getDate();
      // Only move if day actually differs to avoid unnecessary internal re-renders.
      if (!moment(current).isSame(date, "day")) {
        console.log("[Calendar] Prop date changed -> gotoDate", date);
        api.gotoDate(date);
      }
    }, [date]);

    const handleWaitListClose = () => {
      setWaitListOpen(!waitListOpen);
    };

    const createEventId = () => {
      return String(eventGuid++);
    };

    /**
     * Handles creation of new events when user selects time slots in the calendar.
     * Creates a temporary event in the calendar that can then be configured through the AddEventModal.
     * This is triggered when users drag to select a time range on the calendar.
     */
    // Helper function to check if appointments can be created for a user and date
    const canCreateAppointmentForDate = (userId: string, date: string) => {
      const user = propsUsers?.find((u) => u.id === userId);
      if (!user) return false;
      // If user is deactivated, no appointments can be created
      if (user.deletedAt) {
        notificationStore.showMessage(
          "Termine können nicht für deaktivierte Therapeuten erstellt werden.",
          "warning",
        );
        return false;
      }

      // If user has lastWorkDay and the appointment is after it, prevent creation.
      // Day granularity: the last working day itself is still schedulable.
      if (user.lastWorkDay && moment(date).isAfter(user.lastWorkDay, "day")) {
        notificationStore.showMessage(
          `Termine können nicht nach dem letzten Arbeitstag (${moment(user.lastWorkDay).format(
            "L",
          )}) erstellt werden.`,
          "warning",
        );
        return false;
      }
      return true;
    };

    const handleSelect = (selectInfo: DateSelectArg) => {
      console.log("selectInfo", selectInfo);
      const { startStr, endStr, allDay, resource, view } = selectInfo;
      // Check if appointment can be created for this therapist and date
      // In single therapist view, resource.id is a date string, not a user ID
      const selectUserId = CalendarStore.singleTherapistView
        ? CalendarStore.singleTherapistUserId
        : resource?.id;
      if (selectUserId && !canCreateAppointmentForDate(selectUserId, startStr)) {
        return;
      }

      let calendarApi = view.calendar;

      const event = {
        id: createEventId(),
        title: "",
        start: startStr,
        end: endStr,
        resourceId: resource?.id,
        allDay: allDay,
      };
      calendarApi.addEvent(event);
      console.log("event added to calendar", event);
    };

    // Helper to check if an event is locked
    const isEventLocked = (event: CalendarEvent) =>
      (event.appointmentId && lockedAppointments.includes(event.appointmentId)) ||
      (event.id && lockedAppointments.includes(event.id));

    /**
     * Handles clicks on calendar events for appointment selection and UI updates.
     * Manages visual selection state, locks validation, and triggers event content dialogs.
     * Updates selectedAppointmentRef for other operations and highlights the clicked event.
     */
    const handleEventClick = (clickInfo: EventClickArg) => {
      // Clear all participant chip selections when clicking on any event
      if (!(clickInfo.jsEvent.target as HTMLElement).closest("[data-participant-chip]")) {
        const previouslySelected = document.querySelectorAll("[data-participant-chip].selected");
        previouslySelected.forEach((el) => el.classList.remove("selected"));
      }
      const eventId = clickInfo.event.extendedProps.appointmentId || clickInfo.event.id;
      if (eventId && lockedAppointments.includes(eventId)) {
        notificationStore.showMessage("Dieser Termin wird gerade bearbeitet.", "info");
        return;
      }

      // Extract ClientTimeSlot from the new adapter's extendedProps structure
      const extendedProps = clickInfo.event.extendedProps;
      if (extendedProps.patient && extendedProps.therapy) {
        const clientTimeSlot = eventToClientTimeSlot(clickInfo.event as any as CalendarEvent);
        CalendarCommands(CalendarStore).onEventContentClick(clientTimeSlot);
      }

      // Delegate selection to the cell-navigation focus pipeline by focusing
      // the injected TD corresponding to this event's start time and resource.
      const resourceId = clickInfo.event.getResources()[0]?.id;
      const start = clickInfo.event.start;
      if (!resourceId || !start) return;

      const slotTime = moment(start).format("HH:mm:ss");
      const slotMin = timeToSlotMin(slotTime);
      if (slotMin == null) return;

      const cellKey = toCellKey(resourceId, slotMin);
      const cellEl = cellNav.cellKeyToElRef.current.get(cellKey);
      if (cellEl) {
        (cellEl as HTMLElement).focus();
      }
      // Determine patient id from extendedProps (preferred) or appointmentDto (fallback)
      // const extended = clickInfo.event.extendedProps as any;
      // const patientIdFromExtended = extended?.patient?.id;
      // const patientIdFromAppointment = (appointmentDto?.timeSlots?.[0] as any)?.therapyRx?.rx?.patient?.id;
      // const patientId = patientIdFromExtended || patientIdFromAppointment;

      // if (patientId) {
      //   // Update patient context so EventShelfController queries unscheduled appointments for this patient
      //   // setTargetPatientId(patientId);
      //   // setSelectedPatientId(patientId);
      //   // Open the shelf (will now show unscheduled appointments for the selected patient)
      //   // setEventShelfOpen(true);
      // } else {
      //   // If no patient could be resolved we don't force the shelf open (avoids empty query state)
      //   // console.warn("No patient id found on clicked event; EventShelf not opened.");
      // }
    };

    /**
     * Tracks and manages the current state of events in the calendar.
     * Updates the currentEvents state which is used for validation operations
     * like checking for overlapping appointments.
     */
    const minutesSinceMidnight = (date: Date) => date.getHours() * 60 + date.getMinutes();

    const parseTimeStringToMinutes = (time?: string | null) => {
      if (!time) return null;
      const [hours = "0", minutes = "0"] = time.split(":");
      const hoursInt = parseInt(hours, 10);
      const minutesInt = parseInt(minutes, 10);
      if (Number.isNaN(hoursInt) || Number.isNaN(minutesInt)) return null;
      return hoursInt * 60 + minutesInt;
    };

    const handleEvents = (events: EventApi[]) => {
      // console.log('handleEvents', events);
      // console.log(
      // "[CALENDAR EVENT HANDLER] handleEvents",
      // events.map((e) => e.toPlainObject())
      // );

      cellNav.eventSet_attachFocusHandlers(events);
    };

    const getSelectedDate = (): Date => {
      let calendarApi = calendarRef.current?.getApi()!;
      return calendarApi?.getDate();
    };

    // const callbackDatesSet = () => {
    //   console.warn("FULL Calendar has an opinion about the date !!!!!!!!!!!!!");
    //   let date = getSelectedDate();
    //   if (date) setDate(moment(date).format("YYYY-MM-DD"));
    //   return onDatesSet(date);
    // };

    /**
     * Validates and processes new events when they are added to the calendar.
     * Filters out system events (TimeSlots, EventTimeSlots, etc.) that shouldn't be created independently.
     * Creates temporary events that open the AddEventModal for user configuration.
     */
    const handleEventAdd = (eventAddInfo: EventAddArg) => {
      console.log("eventAddInfo", eventAddInfo.event.toPlainObject());

      const invalidTypes = [
        "TimeSlot",
        "EventTimeSlot",
        "ContinuousEventTimeSlot",
        "LeaveOfAbsence",
        "TravelTime",
        "LunchBreak",
        "SimpleWorkingHours",
      ];
      if (
        invalidTypes.includes(
          eventAddInfo.event.extendedProps.type || eventAddInfo.event.extendedProps.timeSlotType,
        )
      ) {
        // Don't allow timeslots to be added independently
        return;
      }

      // Prevent opening modal for events with existing appointment data (drag operations)
      // If the event has an appointmentId or timeSlotId, it's from a drag/receive operation, not a new creation
      if (
        eventAddInfo.event.extendedProps.appointmentId ||
        eventAddInfo.event.extendedProps.timeSlotId ||
        eventAddInfo.event.id !== "0" // ID "0" is assigned to new temporary events from select
      ) {
        // console.log(
        // "[CALENDAR EVENT HANDLER] Ignoring eventAdd for existing appointment during drag/receive:",
        // eventAddInfo.event.id
        // );
        // This is an event from a drag or receive operation, not a new event creation
        return;
      }

      const event = {
        id: undefined, // this is a new event
        title: eventAddInfo.event.title,
        start: eventAddInfo.event.start?.toString()!,
        end: eventAddInfo.event.end?.toString()!,
        resourceId: eventAddInfo.event._def.resourceIds?.toString()!,
        allDay: eventAddInfo.event.allDay,
        timeSlot: {
          start: eventAddInfo.event.start?.toString()!,
          end: eventAddInfo.event.end?.toString()!,
        },
      };
      console.log("add event modal for:", event);
      eventRef.current = event as any as CalendarEvent; // TODO fix this
      setOpenEventModal(true);

      // return onCreateEvent && onCreateEvent(event!);
    };

    /** Highlight / unhighlight calendar DOM elements for the given CalendarEvents */
    const highlightPatientEvents = (events: CalendarEvent[], highlight: boolean) => {
      const api = calendarRef.current?.getApi();
      if (!api) return;
      const ids = new Set(events.map((e) => String(e.id)));
      api.getEvents().forEach((ev) => {
        if (ids.has(ev.id)) {
          if (highlight) {
            ev.setProp("classNames", [...(ev.classNames || []), "patient-duplicate-highlight"]);
          } else {
            ev.setProp(
              "classNames",
              (ev.classNames || []).filter((c: string) => c !== "patient-duplicate-highlight"),
            );
          }
        }
      });
    };

    /** Drop every duplicate highlight currently painted on the grid. */
    const clearDuplicateHighlights = () => {
      const api = calendarRef.current?.getApi();
      if (!api) return;
      api.getEvents().forEach((ev) => {
        if ((ev.classNames || []).includes("patient-duplicate-highlight")) {
          ev.setProp(
            "classNames",
            (ev.classNames || []).filter((c: string) => c !== "patient-duplicate-highlight"),
          );
        }
      });
    };

    /**
     * Show a modal warning about duplicate patient appointments. Returns true if
     * the user confirms.
     *
     * The callbacks are ONE-SHOT: whichever fires first settles the promise and
     * clears the ref. MUI calls `onClose` for Escape and backdrop clicks too, so
     * without this a dialog that was already confirmed could still run the
     * cancel path afterwards — re-highlighting and re-scrolling the grid on a
     * move the user had just approved.
     */
    const showDuplicateWarning = (
      duplicateEvents: CalendarEvent[],
      message: string,
    ): Promise<boolean> => {
      // Highlights from an earlier warning are stale the moment a new one opens.
      clearDuplicateHighlights();
      return new Promise((resolve) => {
        const settle = (result: boolean, after: () => void) => {
          if (duplicateWarningCallbacks.current !== callbacks) return; // already settled
          duplicateWarningCallbacks.current = null;
          duplicateWarningOpenRef.current = false;
          setDuplicateWarning({ open: false, message: "", duplicateEvents: [] });
          after();
          resolve(result);
        };
        const callbacks = {
          onConfirm: () => settle(true, () => highlightPatientEvents(duplicateEvents, false)),
          onCancel: () =>
            settle(false, () => {
              // Highlight the duplicates so the user can see them
              highlightPatientEvents(duplicateEvents, true);
              // Scroll the first highlighted event into view
              requestAnimationFrame(() => {
                const highlightedEl = document.querySelector(".patient-duplicate-highlight");
                if (highlightedEl) {
                  highlightedEl.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              });
            }),
        };
        duplicateWarningCallbacks.current = callbacks;
        duplicateWarningOpenRef.current = true;
        setDuplicateWarning({ open: true, message, duplicateEvents });
      });
    };

    /**
     * Handles single timeslot appointment changes (drag/resize operations).
     * Manages lock validation, working hours validation, therapist credential checks.
     * Delegates to appropriate update functions based on event type and triggers database updates.
     * For multi-timeslot operations, delegates to handleEventDrop instead.
     */
    /**
     * Returns the appointment id of the KGG group the event was dropped into, i.e. a
     * GroupTimeSlotTherapist umbrella in the same column covering exactly the same window.
     */
    const findGroupAppointmentIdAtDropTarget = (event: EventApi): string | undefined => {
      if (!isGroupPatientType(event.extendedProps?.timeSlotType)) return undefined;
      const startMs = event.start?.getTime();
      const endMs = event.end?.getTime();
      if (startMs == null || endMs == null) return undefined;
      const resourceId = event.getResources()[0]?.id;
      const umbrella = (calendarRef.current?.getApi()?.getEvents() || []).find(
        (ev) =>
          ev.id !== event.id &&
          isGroupTherapistType(ev.extendedProps?.timeSlotType) &&
          ev.getResources()[0]?.id === resourceId &&
          ev.start?.getTime() === startMs &&
          ev.end?.getTime() === endMs,
      );
      return umbrella?.extendedProps?.appointmentId;
    };

    const handleEventChange = async (eventChangeInfo: EventChangeArg) => {
      // console.log(
      // "[CALENDAR EVENT HANDLER] eventChangeInfo",
      // eventChangeInfo.event.toPlainObject()
      // );

      // Mark that FullCalendar delivered this move, so the drop safety net
      // (layer 2) knows it does NOT need to recover it. Covers both real drags
      // and our own replayed recovery.
      eventChangeFiredRef.current = true;

      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging. Records that FullCalendar
      // actually delivered the move to the app (before any validation/revert).
      // {
      //   const ep: any = eventChangeInfo.event.extendedProps || {};
      //   const apptId = String(ep.appointmentId || eventChangeInfo.event.id || "");
      //   const toTime = eventChangeInfo.event.start
      //     ? moment(eventChangeInfo.event.start).format("HH:mm")
      //     : "?";
      //   traceChange(apptId, toTime);
      // }

      // locking mechanism:
      // const eventId = eventChangeInfo.event.extendedProps.appointmentId || eventChangeInfo.event.id;
      // if (eventId && lockedAppointments.includes(eventId)) {
      //   return eventChangeInfo.revert();
      // }

      // console.log(
      // "[CALENDAR EVENT HANDLER] handleEventChange event is going to be handed off to controller:",
      // eventChangeInfo.event
      // );

      // Clear original drag time highlight
      if (originalDragTimeRef.current) {
        originalDragTimeRef.current = null;
        setOriginalDragTime(null);
      }

      // todo instead of eventSet_attachFocusHandlers,
      // we need an efficient mutation of the existing map that only chances this event
      const resourceId = eventChangeInfo.event.getResources()[0]?.id;
      // In single therapist view, resources are dates not user IDs
      const userId = CalendarStore.singleTherapistView
        ? (eventChangeInfo.event.extendedProps.user?.id || CalendarStore.singleTherapistUserId)
        : resourceId;

      // Only validate therapist credentials for actual appointments, not system events
      const eventType =
        eventChangeInfo.event.extendedProps.eventType ||
        eventChangeInfo.event.extendedProps.type ||
        eventChangeInfo.event.extendedProps.timeSlotType;

      // Validate credentials only for appointments (not working hours, EventTimeSlots, etc.)
      if (eventType === "appointment" || eventType === "TimeSlot") {
        // Same guard as creation/shelf drops: block moving an appointment onto a
        // deactivated therapist or past the target therapist's last working day.
        // Only enforced when the target resource is a known user (skips e.g. PA columns).
        if (
          userId &&
          eventChangeInfo.event.start &&
          propsUsers?.some((u) => u.id === userId) &&
          !canCreateAppointmentForDate(userId, eventChangeInfo.event.start.toISOString())
        ) {
          return eventChangeInfo.revert();
        }

        const abbreviation = eventChangeInfo.event.extendedProps.therapy?.abbreviation;
        const therapyId = eventChangeInfo.event.extendedProps.therapy?.id;
        // MD-232: a prescription-backed slot is not gated on the line item it
        // currently carries — that one was resolved for the therapist it is
        // moving AWAY from (KG-ZNS on a Bobath therapist carries `GKV_KGN BO`),
        // so testing the target against it rejects every cross-credential move.
        // The backend re-resolves from the Rx's prescribed term on reschedule
        // and answers 409 when the target can deliver none of its variants.
        const hasPrescriptionLine = Boolean(
          eventChangeInfo.event.extendedProps.therapyRxId,
        );

        if (userId && therapyId && !hasPrescriptionLine) {
          const hasCredentials = await hasTherapistCredentials(userId, therapyId);
          if (!hasCredentials) {
            notificationStore.showMessage(
              `Therapeut hat kein Heilmittel für diese Therapie: ${abbreviation}`,
              "warning",
            );
            return eventChangeInfo.revert();
          }
        }

        // Check if patient already has an appointment on the target date (across all therapists)
        // Skip in single therapist view - events are shifted to same display date
        const patientId = eventChangeInfo.event.extendedProps.patient?.id;
        const appointmentId =
          eventChangeInfo.event.extendedProps.appointmentId || eventChangeInfo.event.id;
        /*
          Only a move that LANDS the appointment on a new day can create a
          same-day duplicate. Nudging it within a day it was already on creates
          nothing — the other appointment was there before the drag and is there
          after it — so prompting was both noise and a trap: cancelling reverts
          the event to that same day, and the check would fire again on the
          reverted position, so every "Abbrechen" produced another prompt and
          the appointment ping-ponged between the two times with no way out.
        */
        const previousStart = eventChangeInfo.oldEvent?.start;
        const landsOnANewDay =
          !previousStart ||
          !eventChangeInfo.event.start ||
          !moment(previousStart).isSame(eventChangeInfo.event.start, "day");
        if (
          patientId &&
          eventChangeInfo.event.start &&
          !CalendarStore.singleTherapistView &&
          landsOnANewDay &&
          !duplicateWarningOpenRef.current
        ) {
          const existingAppointments = hasPatientAppointmentOnDate(
            patientId,
            eventChangeInfo.event.start,
            calendarEvents,
            String(appointmentId),
          ).filter(
            // A shared doubled line (unitsPerDay > 1) makes the same-day pair
            // a deliberate Doppelbehandlung, not a duplicate to warn about.
            (e) => !isDoppelPairing(eventChangeInfo.event.extendedProps?.timeSlots, e),
          );
          if (existingAppointments.length > 0) {
            const therapistNames = existingAppointments
              .map((e) => {
                const therapist = e.extendedProps?.user
                  ? `${e.extendedProps.user.firstName || ""} ${e.extendedProps.user.lastName || ""}`.trim()
                  : "Unbekannt";
                const time = e.start ? moment(e.start).format("HH:mm") : "";
                return time ? `${therapist} (${time})` : therapist;
              })
              .join(", ");
            const confirmed = await showDuplicateWarning(
              existingAppointments,
              `Der Patient hat bereits ${existingAppointments.length} Termin(e) an diesem Tag:\n${therapistNames}\n\nMöchten Sie den Termin trotzdem verschieben?`,
            );
            if (!confirmed) {
              return eventChangeInfo.revert();
            }
          }
        }
      }

      (eventChangeInfo as EventChangeArgWithGroupTarget).targetGroupAppointmentId =
        findGroupAppointmentIdAtDropTarget(eventChangeInfo.event);

      return onChangeEvent && onChangeEvent(eventChangeInfo);

      // checks for therapist credentials and working hours are now unreachable until we refactor this:


      // Check if the event is within working hours
      if (isEventOutsideOfWorkingHours(eventChangeInfo.event)) {
        if (
          window.confirm(
            "Der Termin liegt außerhalb der Arbeitszeit, möchten Sie Überstunden ändern oder reparieren?"
          )
        ) {
          // api call to repair overtimes
          repairOvertimes(eventChangeInfo.event, userId);
        }
      }

      const isUserChanged = eventChangeInfo.oldEvent.getResources()[0]?.id !== userId;
      console.log("isUserChanged:", isUserChanged);

      // if (!isUserChanged) {
      //   updateAppoitmentForSingleTimeSlot(eventChangeInfo);
      //   console.log("User has not changed");
      //   return onChangeEvent && onChangeEvent(eventChangeInfo);
      // }



      // updateAppoitmentForSingleTimeSlot(eventChangeInfo);
      // return onChangeEvent && onChangeEvent(eventChangeInfo); // commented out because unreachable
    };

    /**
     * Validates patient availability for route planning view.
     * Checks both patient unavailability schedules and specific unavailable time types.
     * Used in route planning mode to ensure patient is available for proposed appointment times.
     */
    /**
     * Handles removal of events from the calendar.
     * Currently a placeholder function - actual removal logic handled by context menu actions.
     */
    const handleEventRemove = (eventRemoveInfo: EventRemoveArg) => {
      console.log("[CALENDAR EVENT HANDLER] eventRemoveInfo", eventRemoveInfo);
      // return onRemoveEvent && onRemoveEvent(parseInt(eventRemoveInfo.event.id));
    };

    /**
     * Processes external events dropped onto the calendar from the shelf.
     * Validates frequency limits, therapist credentials, and updates appointment data.
     * Handles both regular and route planning modes with different processing logic.
     * Updates appointment times and assignments based on drop location.
     */
    /**
     * Receives external events (shelf drag-drop AND keyboard "belegen" insert).
     * Returns true when the event passed all validations and was handed to the
     * controller; false when it was rejected (the provisional event is removed).
     */
    const handleEventReceive = async (eventReceiveInfo: EventReceiveArg): Promise<boolean> => {
      // Recieve external events
      // console.log(
      // "[CALENDAR EVENT HANDLER] eventReceiveInfo",
      // eventReceiveInfo.event.toPlainObject()
      // );
      const originalEvent = eventReceiveInfo.event;
      const appointmentId = originalEvent.id.toString();
      const date = moment(originalEvent.start).format("YYYY-MM-DD");
      const resourceId = originalEvent.getResources()[0]?.id;
      // Dynamic Template Provider handles (MD-148) are dropped to CREATE a new
      // appointment, so there is no appointment id yet — skip the appointment-id
      // based frequency validation for them further down.
      const isTemplate = !!originalEvent.extendedProps.isTemplate;
      // In single therapist view, resources are dates not user IDs
      const userId = CalendarStore.singleTherapistView
        ? (originalEvent.extendedProps.user?.id || CalendarStore.singleTherapistUserId)
        : resourceId;

      // Prevent internal drags from being treated as external events
      // Check if this event already exists in the calendar (it's an internal drag, not external drop)
      const isInternalEvent = calendarEvents.some(
        (event) =>
          event.id === originalEvent.id || event.extendedProps?.timeSlotId === originalEvent.id,
      );

      if (isInternalEvent) {
        // console.log(
        // "[CALENDAR EVENT HANDLER] Ignoring eventReceive for internal drag (event already exists in calendar):",
        // originalEvent.id
        // );
        // This is an internal drag that was misidentified as external drop
        // The eventChange handler will take care of it, so we just return
        return false;
      }

      // First check if appointment can be created for this date and user
      if (
        userId &&
        !canCreateAppointmentForDate(userId, originalEvent.start?.toISOString() || "")
      ) {
        originalEvent.remove();
        return false;
      }

      // MD-232: credentials are granted per LINE ITEM, so only slots that
      // already resolved to one can be checked here. Anything unresolved is
      // left to the server, which blocks an uncredentialed assignment itself —
      // treating "no id known" as a failure used to reject the drag with an
      // empty therapy name and no way to tell why.
      const abbreviations = slotTherapyList(originalEvent.extendedProps.timeSlots);
      const therapyIds = locallyCheckableTherapyIds(
        originalEvent.extendedProps.timeSlots ?? [],
      );

      const invalidTypes = [
        "TimeSlot",
        "EventTimeSlot",
        "ContinuousEventTimeSlot",
        "LeaveOfAbsence",
        "TravelTime",
        "LunchBreak",
        "SimpleWorkingHours",
      ];

      if (
        invalidTypes.includes(
          originalEvent.extendedProps.type || originalEvent.extendedProps.timeSlotType,
        )
      ) {
        // Don't allow timeslots as external events
        return false;
      }

      // console.log(
      // "[CALENDAR EVENT HANDLER] handleEventReceive event is going to be handed off to controller:",
      // originalEvent
      // );

      try {
        const frequency = !isTemplate && !isContinuous && !CalendarStore.singleTherapistView && (await frequencyValidation(appointmentId, date));
        const credentials =
          therapyIds.length === 0 ? true : await hasTherapistCredentials(userId, therapyIds);
        if (frequency && frequency.validity === "Not Valid") {
          notificationStore.showMessage(frequency.message, "warning");
          originalEvent.remove(); // Remove the event if frequency validation fails
          return false;
        } else if (!credentials) {
          notificationStore.showMessage(
            "Therapeut hat kein Heilmittel für diese Therapie: " + abbreviations,
            "warning",
          );
          originalEvent.remove(); // Remove the event if therapist has no credentials
          return false;
        } else {
          // Check if patient already has an appointment on this date (across all therapists)
          // Skip in single therapist view - events are shifted to same display date.
          // Skip for Dynamic Template Provider drops (MD-148): templates create new
          // appointments via the dedicated controller flow / TemplateScheduleDialog, and a
          // patient legitimately has multiple same-day therapies, so this legacy duplicate-day
          // prompt (designed for moving/placing real appointments) would fire on every drop.
          const patientId =
            originalEvent.extendedProps.timeSlots?.[0]?.patient?.id ||
            originalEvent.extendedProps.patient?.id;
          if (
            patientId &&
            originalEvent.start &&
            !CalendarStore.singleTherapistView &&
            !isTemplate &&
            !duplicateWarningOpenRef.current
          ) {
            const existingAppointments = hasPatientAppointmentOnDate(
              patientId,
              originalEvent.start,
              calendarEvents,
              appointmentId,
            ).filter(
              // A shared doubled line (unitsPerDay > 1) makes the same-day
              // pair a deliberate Doppelbehandlung, not a duplicate.
              (e) => !isDoppelPairing(originalEvent.extendedProps?.timeSlots, e),
            );
            if (existingAppointments.length > 0) {
              const therapistNames = existingAppointments
                .map((e) => {
                  const therapist = e.extendedProps?.user
                    ? `${e.extendedProps.user.firstName || ""} ${e.extendedProps.user.lastName || ""}`.trim()
                    : "Unbekannt";
                  const time = e.start ? moment(e.start).format("HH:mm") : "";
                  return time ? `${therapist} (${time})` : therapist;
                })
                .join(", ");
              const confirmed = await showDuplicateWarning(
                existingAppointments,
                `Der Patient hat bereits ${existingAppointments.length} Termin(e) an diesem Tag:\n${therapistNames}\n\nMöchten Sie den Termin trotzdem planen?`,
              );
              if (!confirmed) {
                originalEvent.remove();
                return false;
              }
            }
          }

          // Clear the original drag time highlight when event is successfully received
          setOriginalDragTime(null);
          if (onEventReceive) {
            await onEventReceive(eventReceiveInfo);
          }
          return true;
        }
      } catch (error) {
        console.error("Error during frequency validation:", error);
        originalEvent.remove(); // Optionally remove the event on error
        return false;
      }
    };

    /**
     * Initiates drag operations for calendar events.
     * Handles special messaging for multi-timeslot appointments and sets up grouped dragging.
     * Shows user feedback when attempting to move complex appointments with multiple time slots.
     */
    const eventDragStart = async (eventDragStartArg: EventDragStartArg) => {
      // console.log(
      // "[CALENDAR EVENT HANDLER] eventDragStart",
      // eventDragStartArg.event.toPlainObject()
      // );

      // Freeze the events array handed to FullCalendar for the duration of this
      // drag so an in-flight cache update can't reprocess and abort the drag.
      beginInteractionFreeze();

      // A previous cancelled duplicate warning paints the events it warned
      // about and has no other moment to unpaint them; starting a new drag is
      // that moment. Otherwise the highlight outlives the warning for the rest
      // of the session.
      clearDuplicateHighlights();

      // Capture the drag source for the drop safety net (layer 2). A new drag
      // cancels any pending recovery from a previous drop and resets the
      // "did FullCalendar deliver the move?" flag.
      if (dropRecoveryTimerRef.current) {
        clearTimeout(dropRecoveryTimerRef.current);
        dropRecoveryTimerRef.current = null;
      }
      eventChangeFiredRef.current = false;
      {
        const ep: any = eventDragStartArg.event.extendedProps || {};
        const apptId = String(ep.appointmentId || eventDragStartArg.event.id || "");
        const p = ep.patient || ep.timeSlots?.[0]?.patient || ep.timeSlots?.[0]?.therapyRx?.rx?.patient;
        const patient = p
          ? `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || String(p.id ?? "?")
          : eventDragStartArg.event.title || "?";
        dragSourceRef.current = {
          eventId: String(eventDragStartArg.event.id || ""),
          apptId,
          patient,
          origStartMs: eventDragStartArg.event.start?.getTime() ?? 0,
          origEndMs: eventDragStartArg.event.end?.getTime() ?? 0,
        };
      }

      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging.
      // {
      //   const ep: any = eventDragStartArg.event.extendedProps || {};
      //   const apptId = String(ep.appointmentId || eventDragStartArg.event.id || "");
      //   const p = ep.patient || ep.timeSlots?.[0]?.patient || ep.timeSlots?.[0]?.therapyRx?.rx?.patient;
      //   const patient = p
      //     ? `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || String(p.id ?? "?")
      //     : eventDragStartArg.event.title || "?";
      //   const fromTime = eventDragStartArg.event.start
      //     ? moment(eventDragStartArg.event.start).format("HH:mm")
      //     : "?";
      //   traceDragStart(apptId, patient, fromTime);
      // }

      // Highlight the original start time while dragging
      if (eventDragStartArg.event.start) {
        const originalTime = moment(eventDragStartArg.event.start).format("HH:mm:ss");
        originalDragTimeRef.current = originalTime;
        // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE/restore before merging.
        // NOTE: Calendar is React.memo, NOT observer — this mobx write does NOT
        // itself re-render Calendar. But `originalDragTime` is a dependency of the
        // `renderSlotLabel` useCallback (slotLabelContent). When Calendar re-renders
        // mid-drag for ANOTHER reason (a prior slow reschedule's onSuccess rebuilds
        // the controller's `calendarEvents` array -> new prop -> React.memo can't
        // bail -> re-render), it re-reads this mobx value; with it set, renderSlotLabel
        // gets a NEW identity -> FullCalendar reprocesses slot labels (the pale-red
        // marker paints = canary) -> that mid-drag reprocess ABORTS the drag.
        // Keeping this unset makes renderSlotLabel's identity stable across those
        // re-renders, closing the slotLabelContent abort path (events array is
        // already frozen separately). Proper fix: paint the marker as a DOM overlay
        // (like useTimeSlotIndicator) so it never flows through slotLabelContent.
        // setOriginalDragTime(originalTime);
      }

      if (eventDragStartArg.event.extendedProps.multipleTimeSlots) {
        console.log("multipleTimeSlots");
        // setMessage("Umzug Termin mit mehreren TimeSlots!");
        // setOpenMessage(true);
        // // make grouped events draggable together
        // if (eventDragStartArg.event.groupId !== undefined)
        //   setGroupCheck(true);
      }
    };

    /**
     * Cleanup operations when drag operations complete.
     * Currently a logging function for debugging drag operations.
     */
    const eventDragStop = (eventDragStopArg: EventDragStopArg) => {
      // console.log("[CALENDAR EVENT HANDLER] eventDragStop", eventDragStopArg.event.toPlainObject());

      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging. Arms the loss detector.
      // {
      //   const ep: any = eventDragStopArg.event.extendedProps || {};
      //   const apptId = String(ep.appointmentId || eventDragStopArg.event.id || "");
      //   traceDragStop(apptId);
      // }

      // Release the events freeze. Deferred one tick so this drop's own
      // eventChange -> optimistic onMutate patch lands before we flush the newest
      // array, and so a back-to-back drag keeps the freeze armed throughout.
      endInteractionFreeze();

      // Arm the drop safety net (layer 2). Capture the pointer-up coordinates now
      // (FullCalendar's jsEvent), then shortly after, if FullCalendar never
      // delivered an eventChange for this drop, recover the move by hit-testing
      // those coordinates against the grid and replaying via the FullCalendar API.
      {
        const js = eventDragStopArg.jsEvent as MouseEvent | undefined;
        const clientX = js?.clientX ?? 0;
        const clientY = js?.clientY ?? 0;
        if (dropRecoveryTimerRef.current) clearTimeout(dropRecoveryTimerRef.current);
        dropRecoveryTimerRef.current = setTimeout(() => {
          dropRecoveryTimerRef.current = null;
          recoverLostDrop(clientX, clientY);
        }, DROP_RECOVERY_DELAY_MS);
      }

      // Clear the original time highlight after drag completes
      originalDragTimeRef.current = null;
      setOriginalDragTime(null);
    };

    /**
     * Validates if an event falls outside therapist working hours.
     * Checks the event time against the therapist's configured work schedule for the day.
     * Used to trigger overtime repair operations when appointments are scheduled outside working hours.
     */
    const isEventOutsideOfWorkingHours = (event: EventImpl): boolean => {
      // TODO implement
      /*
      const hoursForUser = workingHours?.find((wh) => wh.userId === event._def.resourceIds?.toString());
      const workingHours = user?.weeklyWorkDays?.find(
        (w) => w.dayOfWeek === event.start?.getDay() && w.isBreak === false
      );

      if (!workingHours) {
        return false;
      }

      const eventStartTime = moment(event.start).format("HH:mm");
      const eventEndTime = moment(event.end).format("HH:mm");

      const workingStartTime = moment(workingHours.startTime).format("HH:mm");
      const workingEndTime = moment(workingHours.endTime).format("HH:mm");

      const isOutside =
        moment(eventStartTime, "HH:mm").isBefore(moment(workingStartTime, "HH:mm")) ||
        moment(eventEndTime, "HH:mm").isAfter(moment(workingEndTime, "HH:mm"));

      console.log(
        "event times:",
        eventStartTime,
        eventEndTime,
        ", working hours:",
        workingStartTime,
        workingEndTime
      );
      console.log("isOutsideWorkingHours:", isOutside);

      return isOutside;
      */
      return false; // placeholder
    };

    /**
     * Handles overtime repair operations for appointments scheduled outside working hours.
     * Calls backend service to adjust working hours or handle overtime scenarios
     * when appointments are placed outside normal work schedules.
     */
    const repairOvertimes = async (event: EventImpl, userId: string): Promise<any> => {
      const user = users?.find((u) => u.id === userId);
      const start = event.start?.toISOString()!;
      const end = event.end?.toISOString()!;
      console.log("repairing overtimes for event", start, end);

      const result = await adjustOutsideAppointments(userId, start, end);
      console.log("repairing overtimes for event", result);
    };

    const handleCreateEvent = (event: CalendarEvent) => {
      // console.log(
      // "[CALENDAR EVENT HANDLER] handleCreateEvent event is going to be added to the database:",
      // event
      // );
      setOpenEventModal(false);
      return onCreateEvent && onCreateEvent(event);
    };

    const handleEventModalClose = () => {
      console.log("[CALENDAR EVENT HANDLER] handleEventModalClose");
      // remove the event from the calendar
      let calendarApi = calendarRef.current?.getApi()!;
      calendarApi.getEventById("0")?.remove();

      setOpenEventModal(false);
    };

    const handleCloseWarning = () => {
      setWarningDialogMessage("");
    };

    const toggleAppointmentAttended = async (appointment: ClientTimeSlot) => {
      if (!appointment) return;
      contextMenuRef.current?.handleAttended(appointment);
    };

    // Idempotent DOM wiring effect (runs only when resource identities change)
    useEffect(() => {
      const root = calendarRootRef.current;
      if (!root) return;
      if (!resources || resources.length === 0) return;

      const resourcesWithoutPA = resources.filter((r) => r.id !== "0");
      // MD-515: array order, NOT sorted by id. The board renders in array order
      // — `resourceOrder={KEEP_RESOURCE_ARRAY_ORDER}` is an empty spec list
      // precisely so the configured column order reaches the screen — so
      // sorting here handed the keyboard a different order from the one the
      // user sees. Ids are UUIDs, so "one column right" landed somewhere
      // arbitrary. See __tests__/resourceOrder.test.ts, which already records
      // ordering by id as the regression that shipped.
      const resourceIds = resourcesWithoutPA.map((r) => r.id);

      // ✅ set resource ids for navigation
      cellNav.setResources(resourceIds.map((id) => ({ id })));

      // ✅ scope DOM queries to root
      const existingCustomTds = root.querySelectorAll(".fc-timegrid-slot-resource");
      const slotRows = root.querySelectorAll(".fc-timegrid-body tr");

      // ⚠️ resource key alone is not enough; DOM can rebuild without resource change
      // Minimal heuristic: if we already wired and row count is the same and resource key is same, skip.
      const resourceKey = resourceIds.join("|");
      const rowCountKey = String(slotRows.length);
      const wiringKey = `${resourceKey}::${rowCountKey}`;

      if (wiringKey === prevResourceKeyRef.current) {
        return;
      }
      prevResourceKeyRef.current = wiringKey;

      // Removing the cells below drops DOM focus to <body>, which strands the
      // keyboard cursor every time the date window widens. Remember whether the
      // cursor was inside the calendar so it can be restored once rewired.
      const cursorWasFocused = root.contains(document.activeElement);

      // cleanup old TDs (scoped)
      existingCustomTds.forEach((td) => td.remove());

      // clear and rebuild map
      cellNav.cellKeyToElRef.current.clear();

      // IMPORTANT: ensure slot mins are initialized before we start using them
      // If your hook still builds slot mins internally, delete this.
      // Otherwise do: cellNav.ensureSlotMins(slotMinTime, slotMaxTime, 5)

      // Create columns
      let wiredCount = 0;

      slotRows.forEach((row) => {
        const existingTds = row.querySelectorAll("td");
        if (!existingTds.length || existingTds.length > 2) return;

        const lane = row.querySelector(".fc-timegrid-slot-lane") as HTMLElement | null;
        const timeStr = lane?.getAttribute("data-time");
        if (!timeStr) return;

        const slotMin = timeToSlotMin(timeStr);
        if (slotMin == null) return;

        resourceIds.forEach((resourceId) => {
          const td = document.createElement("td");
          td.className = "fc-timegrid-slot fc-timegrid-slot-resource";
          td.dataset.resourceId = resourceId;
          td.dataset.time = timeStr;
          td.dataset.slotMin = String(slotMin);
          td.tabIndex = -1; // ✅ focusable programmatically

          // ✅ store only slotMin-based key
          cellNav.cellKeyToElRef.current.set(toCellKey(resourceId, slotMin), td);

          td.addEventListener("contextmenu", (e) => {
            e.preventDefault();
            handleSlotLaneRightClick(e as unknown as MouseEvent, td as any);
          });

          // td.addEventListener("mouseenter", () => {
          //   setHighlightedTimeSlot(timeStr);
          // });

          row.appendChild(td);
          wiredCount += 1;
        });
      });

      wiredVersionRef.current += 1;

      // setResources (above) re-anchored the logical position to the same date
      // column, so this puts the cursor back exactly where the user left it.
      if (cursorWasFocused) cellNav.refocusCurrentCell();
    }, [resources /* + gridVersion, see note below */]);

    // Add mouse hover highlighting for all calendar cells - uses store actions to avoid re-renders
    // Uses mouseHoverTimeSlot (separate from highlightedTimeSlot used by keyboard navigation)
    useEffect(() => {
      return cellNav.attachMouseHoverListeners((timeSlot) => {
        setMouseHoverTimeSlot(timeSlot);
        setIsMouseHover(true);
      });
    }, [cellNav.attachMouseHoverListeners, setMouseHoverTimeSlot, setIsMouseHover]);

    useEffect(() => {
      document.addEventListener("keydown", cellNav.handleKeyDown);
      return () => document.removeEventListener("keydown", cellNav.handleKeyDown);
    }, [cellNav.handleKeyDown]);

    // Keep ref updated with latest controller data
    useEffect(() => {
      eventShelfControllerRef.current = eventShelfController;
    }, [eventShelfController]);
    useEffect(() => {
      templateProviderControllerRef.current = templateProviderController;
    }, [templateProviderController]);

    // Effect to handle target appointment scrolling and highlighting.
    // Uses a polling mechanism so it works both when already on the calendar page
    // and when navigating here from another page (events may not be loaded yet).
    useEffect(() => {
      const targetAppointmentId = CalendarStore.targetAppointmentId;
      if (!targetAppointmentId || !calendarRootRef.current) return;

      const POLL_INTERVAL_MS = 250;
      const MAX_ATTEMPTS = 24; // up to 6 seconds total
      let attempt = 0;
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const tryHighlight = () => {
        attempt++;
        if (!calendarRootRef.current) return false;

        const allEvents = calendarRef.current?.getApi().getEvents() || [];
        const targetEvent = allEvents.find(
          (e) => (e as any).extendedProps?.appointmentId === targetAppointmentId,
        );

        if (!targetEvent) return false;

        // Use the data-appointment-id attribute stamped by handleEventDidMount
        const targetEventHarness = calendarRootRef.current?.querySelector(
          `.fc-timegrid-event-harness[data-appointment-id="${targetAppointmentId}"]`,
        ) as HTMLElement | null;

        if (!targetEventHarness) return false;

        // --- Found it: scroll, highlight, focus ---

        const timeGridScroller = calendarRootRef.current?.querySelector(
          ".fc-scroller.fc-scroller-liquid-absolute",
        ) as HTMLElement;
        const calendarScrollableWrapper = calendarRootRef.current?.closest(
          ".fc-scroller",
        ) as HTMLElement;
        const outerScrollable = calendarRootRef.current?.parentElement
          ?.parentElement as HTMLElement;

        if (timeGridScroller) {
          const harnessTop = targetEventHarness.offsetTop;
          const scrollerHeight = timeGridScroller.clientHeight;
          const targetScrollTop =
            harnessTop - scrollerHeight / 2 + targetEventHarness.offsetHeight / 2;
          timeGridScroller.scrollTop = Math.max(0, targetScrollTop);
        }

        const scrollHorizontally = (container: HTMLElement | null) => {
          if (container && container.scrollWidth > container.clientWidth) {
            const harnessLeft = targetEventHarness!.offsetLeft;
            const scrollerWidth = container.clientWidth;
            const targetScrollLeft =
              harnessLeft - scrollerWidth / 2 + targetEventHarness!.offsetWidth / 2;
            container.scrollLeft = Math.max(0, targetScrollLeft);
            return true;
          }
          return false;
        };

        scrollHorizontally(calendarScrollableWrapper) ||
          scrollHorizontally(outerScrollable) ||
          scrollHorizontally(calendarRootRef.current?.parentElement as HTMLElement);

        targetEventHarness.classList.add("target-appointment-highlight");

        // Animation is 3s × 4 flashes ≈ keep class for 3.2s
        setTimeout(() => {
          targetEventHarness?.classList.remove("target-appointment-highlight");
        }, 3200);

        if (targetEvent.start) {
          const resourceId = targetEvent.getResources()?.[0]?.id;
          if (resourceId) {
            const slotMin = timeToSlotMin(
              `${targetEvent.start.getHours().toString().padStart(2, "0")}:${targetEvent.start.getMinutes().toString().padStart(2, "0")}`,
            );
            if (slotMin != null) {
              const cellKey = toCellKey(resourceId, slotMin);
              const cellEl = cellNav.cellKeyToElRef.current.get(cellKey);
              if (cellEl) {
                setTimeout(() => cellEl.focus(), 100);
              }
            }
          }
        }

        setTimeout(() => {
          CalendarStore.setTargetAppointmentId(undefined);
        }, 3500);

        return true;
      };

      // First attempt after a short delay for same-page scenarios
      const initialTimer = setTimeout(() => {
        if (tryHighlight()) return;

        // Poll for the event to appear (handles cross-page navigation)
        intervalId = setInterval(() => {
          if (tryHighlight() || attempt >= MAX_ATTEMPTS) {
            if (intervalId) clearInterval(intervalId);
            if (attempt >= MAX_ATTEMPTS) {
              console.warn("[Calendar] Gave up finding target appointment:", targetAppointmentId);
              CalendarStore.setTargetAppointmentId(undefined);
            }
          }
        }, POLL_INTERVAL_MS);
      }, 300);

      return () => {
        clearTimeout(initialTimer);
        if (intervalId) clearInterval(intervalId);
      };
    }, [CalendarStore.targetAppointmentId, cellNav]);

    const planShelfAppointment = async (element: HTMLDivElement, appointment: AppointmentDto) => {
      // Recieve external events
      console.log("plan unscheduled shelf appointment", element);
      console.log("selected shelf appointment", appointment);

      const resourceId = element.closest("td")?.getAttribute("data-resource-id")!;
      // In single therapist view, resources are dates not user IDs
      const userId = CalendarStore.singleTherapistView
        ? CalendarStore.singleTherapistUserId!
        : resourceId;
      const user = users?.find((user) => user.id === userId);
      const abbreviations = slotTherapyList(appointment.timeSlots);
      // Credentials are granted per LINE ITEM (therapy id), so only slots that
      // already resolved to one AND have no prescription line to re-resolve
      // from can be checked here. Everything else is the server's decision.
      const therapyIds = locallyCheckableTherapyIds(appointment.timeSlots);
      const duration = appointment.timeSlots?.reduce(
        // a slot with no line item yet contributes no known duration
        (acc, ts: any) => acc + (slotTherapy(ts)?.duration ?? 0),
        0,
      );
      const patientId = patientAvailability?.patientId;
      const start = moment(dateRef.current + "T" + element.dataset.time).toDate();
      const end = moment(dateRef.current + "T" + element.dataset.time)
        .add(duration, "minutes")
        .toDate();

      // Same guard as drag-drops from the shelf: block planning onto a deactivated
      // therapist or past the target therapist's last working day.
      if (userId && user && !canCreateAppointmentForDate(userId, start.toISOString())) {
        return false;
      }

      // set appointment object with the new start, end times and user
      const updatedAppointment = updateReceivedAppointment(appointment, user?.id!, start, end);

      // console.log(
      // "[CALENDAR EVENT HANDLER] handleAddEvent event is going to be handed off to controller:",
      // appointment
      // );

      // patient events overlapping validation
      const isOverlapping = isPatientAppointmentOverlapping(
        start,
        end,
        calendarEvents,
        appointment,
      );
      if (isOverlapping) {
        console.log("eventAllow", "Patient event is overlapping");
        notificationStore.showMessage(
          "Patienten-Termin überschneidet sich mit einem anderen Termin",
          "warning",
        );
        return false;
      }

      // if (dropInfo.resource?.id === "0" || dropInfo.resource?.id === "1") {
      //   console.log("eventAllow", "add events to PA column is not allowed");
      //   return false;
      // }

      if (patientAvailability && patientId === patientAvailability.patientId) {
        const paStart = moment(patientAvailability.startTime).format("HH:mm");
        const paEnd = moment(patientAvailability.endTime).format("HH:mm");
        const eventStart = moment(start).format("HH:mm");
        const eventEnd = moment(end).format("HH:mm");
        const dayOfWeek = moment(start).weekday() + 1;

        if (patientAvailability.dayOfWeek === dayOfWeek) {
          if (!patientAvailability.startTime || !patientAvailability.endTime) {
            console.log("eventAllow", "Patient is not available in whole day");
            notificationStore.showMessage("Patient ist den ganzen Tag nicht verfügbar", "warning");
            return false;
          }

          if (
            moment(eventStart, "HH:mm").isBetween(
              moment(paStart, "HH:mm"),
              moment(paEnd, "HH:mm"),
            ) ||
            moment(eventEnd, "HH:mm").isBetween(moment(paStart, "HH:mm"), moment(paEnd, "HH:mm"))
          ) {
            // console.log(
            // "eventAllow",
            // "Patient is not available between " + paStart + " and " + paEnd
            // );
            notificationStore.showMessage(
              "Patient ist nicht verfügbar zwischen " + paStart + " und " + paEnd,
              "warning",
            );
            return false;
          }
        }
      }

      for (const patientAvailability of patientUnavailableTypes || []) {
        const paStart = moment(patientAvailability.start);
        const paEnd = moment(patientAvailability.end);
        const eventStart = moment(start);
        const eventEnd = moment(end);

        if (
          paStart.isBefore(eventEnd) &&
          paEnd.isAfter(eventStart) &&
          patientId === patientAvailability.patient?.id
        ) {
          // console.log(
          // "eventAllow",
          // "Patient is not available between " + paStart + " and " + paEnd
          // );
          notificationStore.showMessage(
            "Patient ist nicht verfügbar zwischen " +
              paStart.format("HH:mm") +
              " und " +
              paEnd.format("HH:mm"),
            "warning",
          );
          return false;
        }
      }

      try {
        const frequency =
          !isContinuous &&
          !CalendarStore.singleTherapistView &&
          (await frequencyValidation(
            appointment.id!,
            moment(dateRef.current).format("YYYY-MM-DD"),
          ));
        const credentials =
          !therapyIds || therapyIds.length === 0
            ? true
            : await hasTherapistCredentials(userId, therapyIds);
        if (frequency && frequency.validity === "Not Valid") {
          notificationStore.showMessage(frequency.message, "warning");
          return;
        } else if (!credentials) {
          notificationStore.showMessage(
            "Therapeut hat kein Heilmittel für diese Therapie: " + abbreviations,
            "warning",
          );
          return;
        } else {
          onSelectedShelfEventReceive && onSelectedShelfEventReceive(updatedAppointment!);
        }
      } catch (error) {
        console.error("Error during frequency validation:", error);
        return;
      }
    };

    const renderEventContent = (eventContent: EventContentArg) => {
      // const vm = buildEventViewModel(eventContent.event, lockedAppointments);
      const clientTimeSlot = eventToClientTimeSlot(eventContent.event as unknown as CalendarEvent);

      // console.debug("Rendering event content for:", vm);

      return (
        <EventContent
          clientTS={clientTimeSlot}
          onNoteIconClick={onNoteIconClick}
          onGroupParticipantPatientClick={onGroupParticipantPatientClick}
          onDocumentIconClick={onDocumentIconClick}
          treatmentNotes={treatmentNotes}
        />
      );
    };

    /**
     * Renders therapist column headers with user information and interactive controls.
     * Shows special icons for patient availability columns and user popover controls.
     * In timesheet mode, displays deltaMinutes badge for over/under hours.
     * Provides access to route planning and other user-specific calendar features.
     */
    const resourceLabelContent = (resource: ResourceLabelContentArg) => {
      if (resource.resource.id === "0") return <ScheduleIcon />;

      if (isTimesheetMode) {
        return <ResourceHeaderWithDelta resource={resource} onConfirm={onOutdated} />;
      }

      if (CalendarStore.singleTherapistView) {
        return (
          <ResourceHeaderWithDelta
            resource={resource}
            onConfirm={onOutdated}
            onResourceClick={(dateStr) => {
              CalendarStore.setSelectedDateStr(dateStr);
              CalendarStore.setSingleTherapistView(false);
              CalendarStore.setSingleTherapistUserId(undefined);
            }}
          />
        );
      }

      return <UserPopover resource={resource} onConfirm={onOutdated} />;
    };

    /**
     * Renders custom time slot labels in the calendar.
     * Displays hours and minutes with different opacity for full hours vs partial hours.
     */
    const renderSlotLabel = useCallback(
      (arg: { date: Date }) => {
        const hour = arg.date.getHours();
        const minute = arg.date.getMinutes();
        const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00`;
        const isHighlighted = highlightedTimeSlot === timeString;
        const isOriginalDragTime = originalDragTime === timeString;

        return (
          <TimeSlotLabel
            hour={hour}
            minute={minute}
            isHighlighted={isHighlighted}
            highlightedTimeSlot={highlightedTimeSlot}
            isOriginalDragTime={isOriginalDragTime}
            originalDragTime={originalDragTime}
          />
        );
      },
      [highlightedTimeSlot, originalDragTime],
    );

    const handleContextMenuClose = () => {
      contextMenuRef.current?.closeContextMenu();
    };

    const handleEventClassNames = (arg: { event: EventImpl }) => {
      const classNames: string[] = [];
      if (arg.event.extendedProps.isOutsideWorkingHours) {
        classNames.push("event-outside-working-hours");
      }
      if (arg.event.extendedProps.eventType === "draft") {
        classNames.push(
          arg.event.extendedProps.timeSlotType === "DraftPlacement"
            ? "draft-placement"
            : "draft-shift",
        );
      }
      // Tag 5-minute (or shorter) events so the typography inside can be tightened
      // to fit the compressed slot height (see Calendar.css).
      if (arg.event.start && arg.event.end) {
        const durationMin = (arg.event.end.getTime() - arg.event.start.getTime()) / 60000;
        if (durationMin <= 5) {
          classNames.push("five-min-event");
        }
      }
      return classNames;
    };

    /**
     * Sets up event DOM elements with interactive behaviors after mounting.
     * Attaches click handlers for appointment selection and context menu triggers.
     * Configures right-click context menus for event management operations.
     */

    const handleEventDidMount = (arg: EventMountArg) => {
      // Stamp appointmentId onto the harness element for reliable lookup by the highlight effect
      const apptId = arg.event.extendedProps?.appointmentId;
      if (apptId) {
        const harness = arg.el.closest(".fc-timegrid-event-harness") as HTMLElement;
        if (harness) harness.setAttribute("data-appointment-id", apptId);
      }

      // Warning icon for out-of-hours appointments (styling handled by eventClassNames)
      if (arg.event.extendedProps.isOutsideWorkingHours) {
        const fcTitle = arg.el.querySelector(".fc-event-title");
        if (fcTitle && !fcTitle.querySelector(".warning-icon-outside-hours")) {
          const warningIcon = document.createElement("span");
          warningIcon.className = "warning-icon-outside-hours";
          warningIcon.innerHTML = "⚠️ ";
          warningIcon.style.fontSize = "14px";
          warningIcon.style.marginRight = "4px";
          fcTitle.prepend(warningIcon);
        }
      }

      // ********************* Click handler **************************************
      arg.el.addEventListener("click", (e) => {
        if (arg.event.extendedProps.type === "TimeSlot") {
          // highlight the time label on the left side of the calendar
          const slotTime = moment(arg.event.extendedProps.timeSlot?.start).format("HH:mm:ss");
          setHighlightedTimeSlot(slotTime);

          // Use centralized adapter for conversion
          const clientTimeSlot = eventToClientTimeSlot(arg.event as any as CalendarEvent);
          setModalTS(clientTimeSlot as any); // Cast for compatibility with modal
          return onEventContentClick && onEventContentClick(clientTimeSlot);
        }
      });

      // ********************* Right-click handler **************************************
      arg.el.addEventListener("contextmenu", (e) => {
        // Look up the LIVE event from FullCalendar API instead of using the stale closure-captured arg.event.
        // handleEventDidMount runs once per mount, so arg.event.extendedProps (e.g. participants) would be stale.
        const liveEvent = calendarRef.current?.getApi()?.getEventById(arg.event.id) || arg.event;
        console.debug("Right-click on event:", liveEvent);
        e.preventDefault(); // Prevent default behavior (e.g., opening a context menu)

        // Public holidays are read-only: don't open the (debug) context menu on them.
        if (liveEvent.extendedProps.timeSlotType === "PublicHoliday") {
          return;
        }

        // Check if the click has clientX and clientY properties (to ensure it's a valid click event)
        if (e.clientX && e.clientY) {
          // Create an event object to pass to the menu
          // TODO create proper types for these types of objects that are passed ot he menus
          // or figure out which of the existing types fit best
          let modifiedCalendarEvent: CalendarEvent | undefined = undefined;
          if (isGroupTherapistType(liveEvent.extendedProps.timeSlotType)) {
            // Get fresh shelf data from ref (always current, not from closure)
            const currentShelfData =
              eventShelfControllerRef.current.allShelvesData[0]?.appointments;

            // Filter out participants that are already in pinBoard or shelf
            // Add defensive checks to ensure appointments have valid structure
            const pinBoardData =
              CalendarStore.pinBoardItems?.filter((item: any) => item?.timeSlots && item?.id) || [];

            const shelfAppointments = (currentShelfData || []).filter((item: any) => item?.id);
            const filteredParticipants = liveEvent.extendedProps?.participants?.filter((p: any) => {
              if (!p?.timeSlots?.length) return true; // Keep participants without valid timeslots

              // Check if any participant timeslot ID matches any timeslot ID in pinBoard or shelf
              const participantTimeslotIds = p.timeSlots.map((ts: any) => ts?.id).filter(Boolean);

              return (
                !pinBoardData.some((pd: any) =>
                  pd?.timeSlots?.some((ts: any) => participantTimeslotIds.includes(ts?.id)),
                ) &&
                !shelfAppointments.some((sa: any) =>
                  sa?.timeSlots?.some((ts: any) => participantTimeslotIds.includes(ts?.id)),
                )
              );
            });

            // Create a modified calendar event with filtered participants (deep copy to avoid read-only issues)
            const calendarEvent = eventToCalendarEvent(liveEvent);
            modifiedCalendarEvent = {
              ...calendarEvent,
              extendedProps: {
                ...calendarEvent.extendedProps,
                participants: filteredParticipants || calendarEvent.extendedProps?.participants,
              },
            };
          }
          
          contextMenuRef.current?.openContextMenu(
            modifiedCalendarEvent ? modifiedCalendarEvent : eventToCalendarEvent(liveEvent),
            e.clientX,
            e.clientY,
          );
          setModalTS(liveEvent.extendedProps.timeSlot);
        }
      });

      // ********************* Store element references for keyboard navigation **************************************
      cellNav.eventDidMount_attachElement(arg);
    };
    const onGroupParticipantPatientClick = (
      calendarEvent: CalendarEvent,
      event?: React.MouseEvent,
    ) => {
      // Take the first time slot for this participant

      if (!calendarEvent) return;

      // ONLY proceed if the click is actually on a participant chip
      if (event) {
        const chipElement = (event.target as HTMLElement).closest("[data-participant-chip]");

        if (!chipElement) {
          return; // Exit early
        }

        // Clear all previous participant chip selections
        const previouslySelected = document.querySelectorAll("[data-participant-chip].selected");
        previouslySelected.forEach((el) => el.classList.remove("selected"));

        // Add selection to clicked chip
        chipElement.classList.add("selected");
      } else {
        // No event means this wasn't triggered by a chip click
        return;
      }

      // Highlight the timeline label
      const slotTime = moment(calendarEvent.start).format("HH:mm:ss");
      // highlightTimeLineLabel(slotTime);

      // Convert to client format
      const clientTimeSlot = eventToClientTimeSlot(calendarEvent);

      // Open modal / trigger centralized click handler
      setModalTS(clientTimeSlot as any); // for your modal
      return CalendarCommands(CalendarStore).onEventContentClick(clientTimeSlot);
    };

    // const onGroupParticipantClick = (
    //   participant: AppointmentDto,
    //   e: React.MouseEvent,
    //   therapistEvent: ClientTimeSlot
    // ) => {
    //   try {
    //     console.log("[CALENDAR EVENT Group Participant Click]", participant,e);
    //     e.preventDefault();

    //     const firstTimeSlot = participant.timeSlots?.[0];
    //     if (!firstTimeSlot) {
    //       console.warn("Participant has no timeslots", participant);
    //       return;
    //     }

    //     const patient = firstTimeSlot.therapyRx?.rx?.patient;
    //     const patientName = patient
    //       ? `${patient.lastName}, ${patient.firstName}`
    //       : "Patient";

    //     const calendarEvent: CalendarEvent = {
    //       id: participant.id!,
    //       title: patientName,
    //       start: firstTimeSlot.start!,
    //       end: firstTimeSlot.end,
    //       resourceId: firstTimeSlot.user?.id!,
    //       appointmentId: participant.id,
    //       extendedProps: {
    //         eventType: "appointment",
    //         appointmentId: participant.id,
    //         timeSlotId: firstTimeSlot.id,
    //         timeSlotType: "GroupTimeSlotPatient",
    //         patient: patient,
    //         therapy: slotTherapy(firstTimeSlot),
    //         user: firstTimeSlot.user,
    //         rx: firstTimeSlot.therapyRx?.rx,
    //         hasNotes: (participant.notes?.length || 0) > 0,
    //         isCancelled: participant.end === null,
    //         groupAppointmentId: therapistEvent.appointmentId,
    //         participants: therapistEvent.participants,
    //         attended: firstTimeSlot.attended,
    //         isContinuous: false,
    //       } as any,
    //     };

    //     // Enrich via adapter and pass to context menu
    //     console.debug("Opening context menu for group participant:", calendarEvent);
    //     eventToClientTimeSlot(calendarEvent);
    //     contextMenuRef.current?.openContextMenu(calendarEvent, e.clientX, e.clientY);
    //   } catch (err) {
    //     console.error("Failed to open participant context menu", err);
    //   }
    // };

    const handleRemoveEventTimeSlot = (event: CalendarEvent) => {
      // Lunch breaks are NOT removed optimistically: the deletion can be
      // legally blocked (§ 4 ArbZG). The workingHours query invalidation
      // removes the event on success and keeps it on failure.
      if (event.extendedProps?.timeSlotType !== "LunchBreak") {
        calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
      }
      const timeslotId = event.id!;
      contextMenuRef.current?.closeContextMenu();
      return onRemoveEvent && onRemoveEvent(timeslotId, event);
    };

    const handleRemoveLeaveOfAbsence = (event: CalendarEvent) => {
      calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
      const timeslotId = event.id!;
      contextMenuRef.current?.closeContextMenu();
      return onRemoveEvent && onRemoveEvent(timeslotId, event);
    };

    const handleRemoveSimpleWorkingHours = (event: CalendarEvent) => {
      calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
      const timeslotId = event.id!;
      contextMenuRef.current?.closeContextMenu();
      return onRemoveEvent && onRemoveEvent(timeslotId);
    };

    const handleRemoveContinuousEventTimeSlot = (event: CalendarEvent) => {
      calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
      const timeslotId = event.id!;
      contextMenuRef.current?.closeContextMenu();
      return onRemoveContinuousEvent && onRemoveContinuousEvent(timeslotId);
    };
    const handleRemoveGroupTherapist = (event: CalendarEvent) => {
      calendarRef.current
        ?.getApi()
        ?.getEvents()
        .forEach((e) => {
          if (e.groupId === event.groupId) {
            e.remove();
          }
        });
      contextMenuRef.current?.closeContextMenu();
      return onRemoveGroupTherapist && onRemoveGroupTherapist(event.id!);
    };

    // TODO - figure out what actual event type to use here
    const handleMoveEvent = (event: any) => {
      console.log("Moving event from calendar", event);

      // Highlight the original start time
      if (event.start) {
        const originalTime = moment(event.start).format("HH:mm:ss");
        setOriginalDragTime(originalTime);
      }

      contextMenuRef.current?.closeContextMenu();
      const isGroupPatient =
        isGroupPatientType((event.extendedProps as any)?.timeSlotType) ||
        isGroupPatientType((event.extendedProps as any)?.type);
      const isSingleSlotMove = !!(event.extendedProps as any)?.moveSingleSlot;
      calendarRef.current
        ?.getApi()
        ?.getEvents()
        .forEach((e) => {
          // Same matching rules as useContextMenuController.handleMoveEvent:
          // single-slot moves lift only the clicked slot's event, and events
          // without a groupId (normalized to "") must never be matched by
          // groupId — "" === "" would sweep every groupId-less event (all
          // EventTimeSlots, lunch breaks, …) off the calendar.
          const shouldRemove = isSingleSlotMove
            ? e.id === event.id
            : event.groupId
              ? e.groupId === event.groupId && !isGroupPatient
              : e.id === event.id;
          if (shouldRemove) {
            e.remove();
          }
        });

      // Note: when a group patient is moved out, the therapist event remains on the calendar
      // even if no participants remain. Only explicit deletion (Löschen) should remove it.

      console.log("Termin aus dem kalender entfernt");
      return onMoveEventDate && onMoveEventDate(event);
    };

    /**
     * Handles single clicks on calendar dates/time slots.
     * Used primarily in shelf events mode to trigger context menus for adding events.
     * Sets up mouse position and context data for shelf event operations.
     */
    const handleDateClick = (clickInfo: DateClickArg) => {
      console.log("date click", clickInfo);
      const focused = cellNav.dateClick_HandleEmptyCells(clickInfo.resource?.id, clickInfo.date);

      if (focused && clickInfo.date) {
        const slotTimeStr = moment(clickInfo.date).format("HH:mm:ss");
        setHighlightedTimeSlot(slotTimeStr);
      }
    };

    const handleSlotLaneRightClick = (ev: MouseEvent, slot: HTMLDivElement) => {
      const slotTime = slot.getAttribute("data-time");
      const resourceId = slot.closest("td")?.getAttribute("data-resource-id");
      console.log("Right-click on slot:", slotTime);

      // Combine calendar date with slot time for full ISO datetime
      const dateStr = moment(dateRef.current).format("YYYY-MM-DD");
      console.log("Combined date string:", dateStr);

      // create start to YYYY-MM-DDTHH:mm:ss format
      const start = `${dateStr}T${slotTime}`;
      console.log("Computed start datetime:", start);

      // TODO: implement shelf event context menu when keyboard navigation is done
      // if (ev.clientX && ev.clientY) {
      //   const actionEvt = buildShelfContextAction(dateStr, resourceId!);
      //   contextMenuRef.current?.openContextMenu(adaptContextActionToCalendarEvent(actionEvt), ev.clientX, ev.clientY);
      //   // return onAddShelfEvent && onAddShelfEvent(event)
      // }

      const actionEvt = buildWaitListContextAction(start, resourceId || undefined);
      // console.debug(
      // "[handleSlotLaneRightClick] Opening WaitList context menu with action event",
      // actionEvt
      // );
      contextMenuRef.current?.openContextMenu(
        adaptContextActionToCalendarEvent(actionEvt),
        ev.clientX,
        ev.clientY,
      );
    };

    /**
     * Shows patient and appointment details in tooltips when hovering over events.
     * Constructs tooltip content with patient information, prescription details, and location data.
     * Enhances user experience by providing quick access to appointment context.
     */
    const handleEventMouseEnter = ({ event, el, jsEvent }: EventHoveringArg) => {
      const rx = event.extendedProps.rx as RxDto;
      const patient = event.extendedProps.patient as PatientDto;
      // console.log("event mouse enter", event.toPlainObject(), rx, patient);

      let displayStr = "";
      if (patient) {
        displayStr =
          displayStr +
          `${patient.salutation ? patient.salutation : ""} ${patient.lastName}, ${
            patient.firstName
          }`;
      }
      if (rx) {
        displayStr = displayStr + `\nRezept: ${rx.rxNumber}`;
      }
      // console.log("event mouse enter", event.extendedProps, ts, patient);
      // console.log("tooltip", displayStr);
      el.setAttribute("title", displayStr);
    };
    const handleAddShefEvent = (event: CalendarEvent) => {
      console.log("Adding shelf event", event);
      contextMenuRef.current?.closeContextMenu();
      return onAddShelfEvent && onAddShelfEvent(event as any);
    };

    const onUserChange = (userId: string) => {
      if (userId) {
        // user weekly view - filter to show only the selected user
        const user = users?.find((u) => u.id === userId);
        if (!user) return;
        setSelectedUser(user);
        setIsRoleMode(false); // Disable role mode to use columnUserIds
        setColumnUserIds([userId]); // Update the store to filter resources
        changeCalendarView("resourceTimeGridWeek");
      } else {
        // general daily view - show all users
        setSelectedUser(undefined);
        setColumnUserIds([]); // Clear filter to show all users
        setIsRoleMode(true); // Restore role mode so only therapists with working hours are shown
        changeCalendarView("resourceTimeGridDay");
      }
    };

    const changeCalendarView = (view: string) => {
      let calendarApi = calendarRef.current?.getApi();
      calendarApi?.changeView(view);
      if (view === "resourceTimeGridWeek") {
        calendarApi?.view?.calendar.setOption("dayHeaderFormat", { weekday: "long" });
        calendarApi?.view?.calendar.setOption("hiddenDays", [0, 6]); // Hide Sunday and Saturday
      }
    };

    /**
     * Handles event resize operations with validation.
     * Prevents resizing of non-resizable events and processes valid resize operations.
     * Toggles handleEventChangeForResizing flag to coordinate with change handlers.
     */
    const handleEventResize = (info: EventResizeDoneArg) => {
      // Release the events freeze armed in handleEventResizeStart (deferred one
      // tick so the resize's optimistic onMutate patch lands first).
      endInteractionFreeze();
    };

    const handleEventResizeStart = (info: EventResizeStartArg) => {
      // console.log("Resizing started for event:", info.event.durationEditable,info.event)
      // Freeze the events array for the duration of the resize (same rationale as
      // eventDragStart) so a mid-resize cache update can't abort the interaction.
      beginInteractionFreeze();
    };

    const onNoteIconClick = (event: any, timeSlot: TimeSlotExt) => {
      event.stopPropagation();
      setModalTS(timeSlot);
      if (timeSlot.appointmentId) {
        CalendarStore.openNotesDialog(timeSlot.appointmentId);
      }
    };

    const onDocumentIconClick = async (event: any, timeSlot: TimeSlotExt) => {
      event.stopPropagation();
      setModalTS(timeSlot);
      if (timeSlot.id) {
        const data: TreatmentNoteDTO[] | undefined =
          await clinicalDocumentation.getClinicalTreatmentNotesByTreatmentId(timeSlot.id);
        const notesText = data
          ? data.map((note) => note.note).join("\n\n")
          : "Keine Dokumentation vorhanden";
        setTreatmentNotes(notesText);
      }
    };

    /**
     * Validates whether events can be dropped at specific calendar locations.
     * Checks for patient event overlaps, patient availability, and resource restrictions.
     * Prevents events from being dropped in invalid locations like patient availability columns.
     */
    const eventAllow = (dropInfo: DateSpanApi, draggedEvent: EventApi | null): boolean => {
      // Belt-and-braces: long/multi-day LeaveOfAbsence blocks are never drag-movable.
      // The timeslot adapter already renders them with editable: false, but any such
      // event that reaches a drag through another code path must be rejected here —
      // a stray drop silently PATCHes the absence and triggered the "disappearing
      // vacation" incident. Short (< 12h) absences stay draggable; long ones are
      // edited via the AbsenceDialog only. Unknown duration → blocked (defensive).
      if (draggedEvent?.extendedProps?.timeSlotType === "LeaveOfAbsence") {
        const startMs = draggedEvent.start?.getTime();
        const endMs = draggedEvent.end?.getTime();
        const durationHours =
          startMs != null && endMs != null ? (endMs - startMs) / (60 * 60 * 1000) : NaN;
        if (!(durationHours < DRAGGABLE_LOA_MAX_HOURS)) {
          console.log("eventAllow", "Long LeaveOfAbsence blocks cannot be moved by drag");
          return false;
        }
      }

      const start = moment(dropInfo.start).toDate();
      const end = moment(dropInfo.end).toDate();

      // patient events overlapping validation
      // Skip in single therapist view - events from different days share the same display date
      const currentEvents = calendarRef.current?.getApi()?.getEvents() || [];
      if (!CalendarStore.singleTherapistView) {
        const isOverlapping = isPatientEventOverlapping(start, end, currentEvents, draggedEvent!);
        if (isOverlapping) {
          console.log("eventAllow", "Patient event is overlapping");
          // notificationStore.showMessage("Patienten-Termin überschneidet sich mit einem anderen Termin", "warning");
          return false;
        }
      }

      if (dropInfo.resource?.id === "0" || dropInfo.resource?.id === "1") {
        console.log("eventAllow", "add events to PA column is not allowed");
        return false;
      }

      const patientId = parseInt(draggedEvent?.extendedProps.patient?.id) || 0;

      // For KGG group appointments, prevent duplicate patients being scheduled in the same time period.
      // The dragged event is a GroupTimeSlotPatient; check if any overlapping GroupTimeSlotTherapist
      // in currentEvents already has this patient as a participant.
      const draggedPatientId = parseInt(draggedEvent?.extendedProps?.patient?.id) || 0;
      // Shelf drops carry the slots in `timeSlots`; events already on the calendar
      // (e.g. a cancelled participant shown via the Stornierungen toggle) only
      // carry `timeSlotType`.
      const isGroupPatientDrag =
        isGroupPatientType(draggedEvent?.extendedProps?.timeSlots?.[0]?.type) ||
        isGroupPatientType(draggedEvent?.extendedProps?.timeSlotType);

      if (draggedPatientId && isGroupPatientDrag) {
        const overlappingTherapistEvents = currentEvents.filter((ev) => {
          if (ev.id === draggedEvent?.id) return false;
          if (!isGroupTherapistType(ev.extendedProps?.timeSlotType)) return false;
          // In single therapist view, events from different days are all shifted to the
          // same display date and columns represent dates — only compare events in the
          // same day column, otherwise a group on another day looks falsely overlapping.
          if (CalendarStore.singleTherapistView) {
            const evResourceId = (ev as any).getResources?.()[0]?.id;
            if (evResourceId && dropInfo.resource?.id && evResourceId !== dropInfo.resource.id) {
              return false;
            }
          }
          const evStart = ev.start;
          const evEnd = ev.end;
          if (!evStart || !evEnd) return false;
          return evStart < end && evEnd > start;
        });

        for (const ev of overlappingTherapistEvents) {
          const participants: any[] = ev.extendedProps?.participants || [];
          const isDuplicate = participants.some((p: any) => {
            const participantPatientId =
              parseInt(p.timeSlots?.[0]?.therapyRx?.rx?.patient?.id) || 0;
            return participantPatientId === draggedPatientId;
          });

          if (isDuplicate) {
            console.log("eventAllow", "Duplicate patient in KGG group - not allowed");
            notificationStore.showMessage(
              "Patient ist bereits in diesem Zeitraum eingeplant",
              "warning",
            );
            return false;
          }
        }
      }

      if (!checkPatientAvailability(patientId, start, end)) {
        return false;
      }

      return true;
    };

    /**
     * Validates patient availability for appointment scheduling.
     * Checks both general patient availability windows and specific unavailable time types.
     * Prevents appointments from being scheduled when patients are not available.
     */
    const checkPatientAvailability = (patientId: number, start: Date, end: Date): boolean => {
      // console.log("checkPatientAvailability", patientId, start, end);
      if (isContinuous) return true; // skip availability check in continuous view

      const startDateOnly = moment(start).format("YYYY-MM-DD");
      const patientAvailability = patientAvailabilities?.find(
        (pa) =>
          pa.dayOfWeek === getSelectedDate().getDay() &&
          (!pa.validityStart || moment(pa.validityStart).format("YYYY-MM-DD") <= startDateOnly) &&
          (!pa.validityEnd || moment(pa.validityEnd).format("YYYY-MM-DD") >= startDateOnly),
      );

      if (patientAvailability && patientId === patientAvailability.patientId) {
        const paStart = moment(patientAvailability.startTime).format("HH:mm");
        const paEnd = moment(patientAvailability.endTime).format("HH:mm");
        const eventStart = moment(start).format("HH:mm");
        const eventEnd = moment(end).format("HH:mm");
        const dayOfWeek = moment(start).weekday() + 1;

        if (patientAvailability.dayOfWeek === dayOfWeek) {
          if (!patientAvailability.startTime || !patientAvailability.endTime) {
            console.log("eventAllow", "Patient is not available in whole day");
            notificationStore.showMessage("Patient ist den ganzen Tag nicht verfügbar", "warning");
            return false;
          }

          if (
            moment(eventStart, "HH:mm").isBetween(
              moment(paStart, "HH:mm"),
              moment(paEnd, "HH:mm"),
            ) ||
            moment(eventEnd, "HH:mm").isBetween(moment(paStart, "HH:mm"), moment(paEnd, "HH:mm"))
          ) {
            // console.log(
            // "eventAllow",
            // "Patient is not available between " + paStart + " and " + paEnd
            // );
            notificationStore.showMessage(
              "Patient ist nicht verfügbar zwischen " + paStart + " und " + paEnd,
              "warning",
            );
            return false;
          }
        }
      }

      for (const patientAvailability of patientUnavailableTypes || []) {
        const paStart = moment(patientAvailability.start);
        const paEnd = moment(patientAvailability.end);
        const eventStart = moment(start);
        const eventEnd = moment(end);

        if (
          paStart.isBefore(eventEnd) &&
          paEnd.isAfter(eventStart) &&
          patientId === patientAvailability.patient?.id
        ) {
          // console.log(
          // "eventAllow",
          // "Patient is not available between " + paStart + " and " + paEnd
          // );
          notificationStore.showMessage(
            "Patient ist nicht verfügbar zwischen " +
              paStart.format("HH:mm") +
              " und " +
              paEnd.format("HH:mm"),
            "warning",
          );
          return false;
        }
      }

      return true;
    };

    /**
  x^   * Controls which events can overlap with others in the calendar.
     * Allows background events (like heat treatments) to overlap with regular events.
     * Prevents regular appointments from overlapping unless they are group time slots.
     */
    const handleEventOverlap = (stillEvent: EventApi, movingEvent: EventApi | null): boolean => {
      console.log("handleEventOverlap", stillEvent.toPlainObject(), movingEvent?.toPlainObject());

      if (stillEvent.display === "background" || movingEvent?.display === "background") {
        return true;
      }

      // Hidden GroupTimeSlotPatient events (display: "none") are existing members of a
      // group appointment rendered under the visible GroupTimeSlotTherapist umbrella.
      // They share the same time range, so FC also calls eventOverlap against them —
      // ignore them, the umbrella event already governs the decision.
      if (stillEvent.display === "none" || movingEvent?.display === "none") {
        return true;
      }

      // Allow working hours and lunch breaks to overlap with each other
      const stillEventType = stillEvent.extendedProps?.eventType;
      const movingEventType = movingEvent?.extendedProps?.eventType;

      // Allow appointments to be within working hours (appointments should overlap with working hours)
      if (
        (stillEventType === "working-hours" && movingEventType !== "working-hours") ||
        (stillEventType !== "working-hours" && movingEventType === "working-hours")
      ) {
        return true;
      }

      if (stillEventType === "working-hours" && movingEventType === "working-hours") {
        // Working hours with lunch breaks should overlap (lunch breaks are within working hours)
        const stillTimeSlotType = stillEvent.extendedProps?.timeSlotType;
        const movingTimeSlotType = movingEvent?.extendedProps?.timeSlotType;

        if (
          stillTimeSlotType === "LunchBreak" ||
          movingTimeSlotType === "LunchBreak" ||
          stillTimeSlotType === "WorkingHours" ||
          movingTimeSlotType === "WorkingHours"
        ) {
          return true;
        }
      }

      if (movingEvent && movingEvent.extendedProps) {
        const timeSlots = movingEvent.extendedProps.timeSlots || [
          movingEvent.extendedProps.timeSlot,
        ];

        const isMovingGroupPatient = timeSlots?.some(
          (slot: any) =>
            isGroupPatientType(slot?.type) ||
            isGroupPatientType(
              movingEvent.extendedProps?.timeSlotType ||
                movingEvent.extendedProps?.timeSlots?.[0]?.type,
            ),
        );

        if (isMovingGroupPatient) {
          // KGG group patient events can only overlap with a GroupTimeSlotTherapist
          // that has the exact same start and end time
          const isStillGroupTherapist = isGroupTherapistType(
            stillEvent.extendedProps?.timeSlotType,
          );
          if (isStillGroupTherapist) {
            const sameStart = stillEvent.start?.getTime() === movingEvent.start?.getTime();
            const sameEnd = stillEvent.end?.getTime() === movingEvent.end?.getTime();
            console.log("handleEventOverlap", "isStillGroupTherapist, returning", sameStart && sameEnd);

            return sameStart && sameEnd;
          }
          console.log("handleEventOverlap", "not a group therapist - not allowing overlap", stillEvent.extendedProps?.timeSlotType);
          // Not a group therapist → don't allow overlap
          return false;
        }
      }

      console.log("handleEventOverlap", "Appointment cannot overlap");
      // notificationStore.showMessage("Termin kann nicht überlappen", "warning");
      return false;
    };

    // Keep the drop-recovery validator pointing at the CURRENT-render
    // eventAllow / handleEventOverlap closures (which read fresh props/state).
    // Mirrors FullCalendar's drag-time decision: a drop is valid only if
    // eventAllow passes AND no same-resource, time-overlapping event disallows it
    // via the eventOverlap policy. Used by recoverLostDrop before replaying.
    validateRecoveryDropRef.current = (
      ev: EventApi,
      start: Date,
      end: Date,
      resourceId: string | null,
    ): boolean => {
      const dropInfoLike = {
        start,
        end,
        resource: resourceId ? { id: resourceId } : undefined,
      } as unknown as DateSpanApi;

      if (!eventAllow(dropInfoLike, ev)) return false;

      // handleEventOverlap reads movingEvent.start/end (the group-patient branch
      // requires an EXACT start/end match with the GroupTimeSlotTherapist
      // umbrella). At validation time `ev` has NOT been moved yet, so its
      // start/end are still the ORIGIN times. Pass a shim carrying the TARGET
      // times (delegating display/extendedProps to the real event) so the group
      // same-slot check is evaluated against where the drop is actually landing.
      const movingShim = {
        display: ev.display,
        extendedProps: ev.extendedProps,
        start,
        end,
        toPlainObject: () => ev.toPlainObject(),
      } as unknown as EventApi;

      const all = calendarRef.current?.getApi()?.getEvents() ?? [];
      for (const still of all) {
        if (still.id === ev.id) continue;
        if (!still.start || !still.end) continue;
        // Only events in the same therapist column can collide.
        const stillResourceId = still.getResources()?.[0]?.id;
        if (resourceId && stillResourceId && stillResourceId !== resourceId) continue;
        // Geometric time overlap with the target span.
        if (still.start < end && still.end > start) {
          if (!handleEventOverlap(still, movingShim)) return false;
        }
      }
      return true;
    };


    /**
     * Controls which calendar slots can be selected for new event creation.
     * Prevents selection in shelf events mode and patient availability columns.
     * Enables time slot selection for regular appointment scheduling.
     */
    const handleSelectable = (selectInfo: DateSpanApi) => {
      if (selectInfo.resource?.id === "0") {
        console.log("selectable", "select in PA column is not allowed");
        return false;
      }
      return true;
    };

    /**
     * Styles resource header elements after they are mounted in the DOM.
     * Applies special styling to patient availability column headers.
     * Ensures consistent visual appearance for different resource types.
     */
    const handleResourceLabelDidMount = (arg: ResourceLabelMountArg) => {
      if (arg.resource.id === "0") {
        arg.el.style.backgroundColor = mobiliTheme.palette.secondary.light;
      }
    };

    const handleCloseNotesDialog = () => {
      onOutdated();
    };

    /**
     * Adjusts DOM structure and layout after calendar view changes.
     * Modifies table cell structure for proper display and keyboard navigation.
     * Ensures time column layout remains consistent across view transitions.
     */
    const handleViewDidMount = (view: any) => {
      setTimeout(() => {
        adjustTableTimeCells();
      }, 0);
    };

    const adjustTableTimeCells = () => {
      // Scope DOM queries to current calendar instance to avoid conflicts during view transitions
      const root = calendarRootRef.current;
      if (!root) return;

      const rows = root.querySelectorAll(".fc-timegrid-body tr");

      // Iterate through every 3 rows
      for (let i = 0; i < rows.length; i += 3) {
        // Get the first `<td>` of the current row
        const currentCell = rows[i].querySelector("td:first-child") as HTMLElement;
        if (currentCell) {
          // Set `rowspan` to 3
          currentCell.setAttribute("rowspan", "3");
        }

        // Hide the first `<td>` in the next two rows
        for (let j = 1; j < 3; j++) {
          if (rows[i + j]) {
            const nextCell = rows[i + j].querySelector("td:first-child") as HTMLElement;
            if (nextCell) {
              nextCell.style.display = "none";
            }
          }
        }
      }
    };

    // Publish the latest handler closures for the stable trampolines to forward to.
    // Runs every render so the trampolines always invoke current logic.
    handlersRef.current = {
      handleSelect,
      handleSelectable,
      handleDateClick,
      handleEventAdd,
      handleEventChange,
      handleEventRemove,
      handleEventReceive,
      handleEventResize,
      handleEventResizeStart,
      handleEventClick,
      handleEventDidMount,
      handleEventMouseEnter,
      eventDragStart,
      eventDragStop,
      eventAllow,
      handleEventOverlap,
      renderEventContent,
      handleEventClassNames,
      handleEvents,
      resourceLabelContent,
      handleResourceLabelDidMount,
      handleViewDidMount,
      eventWillUnmount: cellNav.eventWillUnmount_detachElement,
    };

    // ----- memoized FullCalendar element (prevention layer / layer 1) -----
    //
    // Recompute ONLY when real calendar data changes: the (freeze-controlled)
    // events snapshot, the resource columns, the title format, or the reactive
    // slot-label renderer (highlight repaint — never changes during a drag). All
    // config props are module-scope constants and all handlers are stable
    // trampolines, so unrelated internal-state re-renders (modals, tooltips,
    // selectedUser, waitlist, …) reuse the SAME element and FullCalendar never
    // reprocesses — which is what was aborting in-flight drags.
    //
    // NOTE: `renderSlotLabel` depends on `highlightedTimeSlot` AND `originalDragTime`.
    // The drag MARKER (originalDragTime) is intentionally disabled; do NOT re-enable
    // it by feeding originalDragTime through this path, or the element will recompute
    // mid-drag and re-introduce the abort. Re-enable the marker via a decoupled DOM
    // overlay instead (see useTimeSlotIndicator for the pattern).
    const calendarElement = useMemo(
      () =>
        dateRef.current ? (
          <FullCalendar
            height="100%"
            ref={calendarRef}
            locale={"de"}
            initialDate={dateRef.current}
            schedulerLicenseKey="0067980715-fcs-1697454050"
            plugins={CALENDAR_PLUGINS}
            headerToolbar={false}
            titleFormat={titleFormat}
            buttonText={CALENDAR_BUTTON_TEXT}
            initialView="resourceTimeGridDay"
            views={CALENDAR_VIEWS}
            slotDuration={"00:05"}
            slotLabelInterval={"00:05"}
            slotLabelFormat={SLOT_LABEL_FORMAT}
            eventTimeFormat={EVENT_TIME_FORMAT}
            slotMinTime={SLOT_MIN_TIME}
            slotMaxTime={SLOT_MAX_TIME}
            firstDay={1}
            eventMinHeight={1}
            defaultTimedEventDuration={"00:30:00"}
            editable={true}
            eventResizableFromStart={true}
            eventResizeStart={stableHandlers.handleEventResizeStart}
            selectable={true}
            selectAllow={stableHandlers.handleSelectable}
            selectMirror={true}
            dayMaxEvents={true}
            droppable={true}
            nowIndicator={true}
            allDaySlot={false}
            resources={resources}
            resourceOrder={KEEP_RESOURCE_ARRAY_ORDER}
            events={displayedEvents}
            eventInteractive={true}
            select={stableHandlers.handleSelect}
            selectMinDistance={10}
            dateClick={stableHandlers.handleDateClick}
            eventAdd={stableHandlers.handleEventAdd}
            eventChange={stableHandlers.handleEventChange}
            eventRemove={stableHandlers.handleEventRemove}
            eventReceive={stableHandlers.handleEventReceive}
            eventResize={stableHandlers.handleEventResize}
            eventClick={stableHandlers.handleEventClick}
            eventDidMount={stableHandlers.handleEventDidMount}
            eventMouseEnter={stableHandlers.handleEventMouseEnter}
            eventDragStart={stableHandlers.eventDragStart}
            eventDragStop={stableHandlers.eventDragStop}
            eventAllow={stableHandlers.eventAllow}
            eventOverlap={stableHandlers.handleEventOverlap}
            eventContent={stableHandlers.renderEventContent}
            eventClassNames={stableHandlers.handleEventClassNames}
            eventsSet={stableHandlers.handleEvents}
            resourceLabelContent={stableHandlers.resourceLabelContent}
            resourceLabelDidMount={stableHandlers.handleResourceLabelDidMount}
            viewDidMount={stableHandlers.handleViewDidMount}
            eventWillUnmount={stableHandlers.eventWillUnmount}
            slotLabelContent={renderSlotLabel}
          />
        ) : null,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [displayedEvents, resources, titleFormat, renderSlotLabel],
    );

    return (
      <>
        <LunchBreakPromptDialog />
        <HorizonWarningDialog />

        <WarningDialog
          open={!!openWarningDialog}
          onClose={onCloseWarningDialog || handleCloseWarning}
          title="Besuchen Sie Gap für einen Termin"
          content={warningMessage || ""}
          typeToProceed={["Ok"]}
        />

        <WarningDialog
          open={duplicateWarning.open}
          onClose={() => duplicateWarningCallbacks.current?.onCancel()}
          onContinue={() => duplicateWarningCallbacks.current?.onConfirm()}
          title="Doppelter Termin am selben Tag"
          content={<span style={{ whiteSpace: "pre-line" }}>{duplicateWarning.message}</span>}
        />

        <Grid
          container
          direction={"column"}
          sx={{ border: "0px dotted black", height: "100%", overflow: "hidden" }}
        >
          <Grid
            item
            sx={{
              border: "0px solid blue",
              display: "flex",
              position: "sticky",
              top: 0,
              width: "100%",
              maxWidth: "100%",
              // Was 75px, added without comment by MOB-753 (horizontal scroll)
              // and nothing in the current layout needs it: no control sits in
              // that strip, and the calendar body below runs to the full width,
              // so it was not aligning the toolbar to anything either. It just
              // shortened the row — which is why width clawed back inside the
              // toolbar kept turning into dead space on the right instead of
              // room for the controls.
              paddingRight: "8px",
              boxSizing: "border-box",
              zIndex: 2,
              flexShrink: 0,
            }}
          >
            <HeaderToolbar
              calendarRef={calendarRef}
              onDatesSet={onDatesSet}
              onOutdated={onOutdated}
              users={users}
              onUserChange={(userId) => onUserChange(userId)}
              getCellSelectionRef={internalGetCellSelectionRef}
              keyboardNavigationRef={toolbarNavigationRef}
            />
          </Grid>
          {isViewPastHorizon && (
            <Grid item sx={{ flexShrink: 0, width: "100%", boxSizing: "border-box", px: 1, pb: 0.5 }}>
              <Alert
                severity="warning"
                action={
                  horizonDateStr ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => CalendarStore.setSelectedDateStr(horizonDateStr)}
                    >
                      Zurück zum Horizont
                    </Button>
                  ) : undefined
                }
                sx={{ py: 0, alignItems: "center" }}
              >
                Sie planen über den Planungshorizont
                {horizonDateStr ? ` (${moment(horizonDateStr).format("DD.MM.YYYY")})` : ""} hinaus.
              </Alert>
            </Grid>
          )}
          <Grid item sx={{ flexGrow: 1, position: "relative", overflow: "hidden", minWidth: 0 }}>
            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "auto" }}>
            <TopLevelPaper
              ref={calendarRootRef}
              sx={{
                flexGrow: 1,
                minWidth: calendarContentWidth,
                height: "100%",
              }}
            >
              {calendarElement}
            </TopLevelPaper>
            </Box>
          </Grid>
        </Grid>

        {/* dialogues, models, menues */}
        {openEventModal && (
          <AddEventModal
            event={eventRef.current as any}
            open={openEventModal}
            onConfirm={handleCreateEvent as any}
            onClose={handleEventModalClose}
            continuous={isContinuous}
            user={users?.find((user) => user.id === (
              CalendarStore.singleTherapistView
                ? CalendarStore.singleTherapistUserId
                : eventRef.current?.resourceId
            ))}
            isTimesheetMode={isTimesheetMode}
          />
        )}
        {/* <Backdrop
          open={showLoading}
          style={{ zIndex: 1300, color: mobiliTheme.palette.primary.light }}
        >
          <CircularProgress color="inherit" />
        </Backdrop> */}
        <ContextMenuWithController
          ref={contextMenuRef}
          handlers={{
            onRemoveEvent: (timeslotId: string, event?: CalendarEvent) => {
              // If event is provided, use it directly; otherwise find it
              const targetEvent =
                event || calendarEvents.find((e) => e.extendedProps?.timeSlotId === timeslotId);
              if (targetEvent) handleRemoveEventTimeSlot(targetEvent);
            },
            onRemoveWorkingHours: onRemoveWorkingHours || (() => {}),
            onRemoveContinuousEvent: (timeslotId: string) => {
              // Find the event by timeslotId and call original handler
              const event = calendarEvents.find((e) => e.extendedProps?.timeSlotId === timeslotId);
              if (event) handleRemoveContinuousEventTimeSlot(event);
            },
            onRemoveGroupTherapist: (timeslotId: string) => {
              // Find the event by timeslotId and call original handler
              const event = calendarEvents.find((e) => e.extendedProps?.timeSlotId === timeslotId);
              if (event) handleRemoveGroupTherapist(event);
            },
            onMoveEventDate: handleMoveEvent,
            onAddShelfEvent: handleAddShefEvent,
            onOutdated: onOutdated, // TODO: implement if needed
            onCloseNotesDialog: handleCloseNotesDialog,
            setSelectedPatientId: (id: number) => setSelectedPatientId(id),
            calendarRef: calendarRef,
            calendarEvents: calendarEvents,
            isContinuous,
            onCancelDayClick,
          }}
          clickedTS={clickedTS}
        />

        <TimeSlotDialog open={isModalOpen} setOpen={setModalOpen} timeSlot={clickedTS} />
        {/* Mouse-driven time slot indicator (horizontal line + label) - observes CalendarStore */}
        <TimeSlotIndicator calendarRootRef={calendarRootRef} />
        {/* Sticky TimeSlotLabel that appears when scrolling horizontally - observes CalendarStore */}
        <StickyTimeSlotLabel calendarRootRef={calendarRootRef} />
      </>
    );
  },
);
Calendar.displayName = "Calendar";

export default Calendar;
